import { Injectable } from '@angular/core';
// To save files
import { saveAs } from 'file-saver';

// pdfmake
import pdfMake from 'pdfmake/build/pdfmake';
// @ts-expect-error works
import * as pdfFonts from '../../../themes/font/vfs_fonts_inter';

// Excel
import ExcelJS, * as Excel from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Moment } from 'moment';
import { BehaviorSubject } from 'rxjs';
import { Room } from '../models/room.model';

interface IntervenantColumn {
  groupId: string;
  groupName: string;
  header: string;
  formationIds: string[];
}

interface DailyDataEntry {
  section: 'BLOC' | 'HORS_SALLE' | 'CONSULTATION' | 'EXTRA_CLINIQUE' | 'COMMENT';
  salle?: string;
  vacationLines?: string[];
  specialtyColors?: (string | undefined)[];
  commentPerRoom?: string;
  intervenantsPerGroupByVacation?: { [groupId: string]: string }[];
  label?: string;
  comment?: string;
  vacation?: string;
  ibodeIde?: string;
  iades?: string;
  anesth?: string;
}

const COLORS = {
  headerBg: [0, 0, 0] as [number, number, number],
  headerText: [255, 255, 255] as [number, number, number],
  subHeaderBg: [173, 198, 232] as [number, number, number],
  subHeaderText: [0, 0, 0] as [number, number, number],
  evenRow: [255, 255, 255] as [number, number, number],
  oddRow: [250, 250, 250] as [number, number, number],
  sectionBg: [173, 198, 232] as [number, number, number],
  borderColor: [0, 0, 0] as [number, number, number],
  horsAlleBg: [255, 255, 255] as [number, number, number],
  commentBg: [245, 245, 245] as [number, number, number],
};

const FONT_SIZES = {
  title: 14,
  subtitle: 12,
  header: 11,
  cell: 8.7,
  small: 8.7,
};

interface StyledRun {
  text: string;
  bold: boolean;
  italic: boolean;
  color: [number, number, number];
  fontSize?: number;
}

type StyledLine = StyledRun[];

interface WordToken {
  text: string;
  run: StyledRun;
  width: number;
}

interface VisualSegment {
  text: string;
  run: StyledRun;
}

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  constructor() {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
    pdfMake.fonts = {
      Inter: {
        normal: 'Inter-Regular.ttf',
        bold: 'Inter-Bold.ttf',
        italics: 'Inter-Italic.ttf',
      },
    };
  }

  private currentPopulationId = new BehaviorSubject<string>('none');

  setPopulation(id: string) {
    this.currentPopulationId.next(id);
  }

  getPopulationId(): string {
    return this.currentPopulationId.value;
  }

  private saveAsFile(buffer: any, fileName: string, fileType: string): void {
    const data: Blob = new Blob([buffer], { type: fileType });
    saveAs(data, fileName);
  }

  exportPDF(docDefinition: any, fileName: string): void {
    pdfMake.createPdf(docDefinition).download(fileName);
  }

  async exportExcelSkills(rows: string[][], fileName: string): Promise<void> {
    const workbook: Excel.Workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet('Effectifs', {
      views: [{ state: 'frozen', ySplit: 1, activeCell: 'A1', showGridLines: true }],
    });

    // Start of header
    const headerRow = worksheet.addRow(rows[0]);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    for (let i = 1; i <= rows[0].length; i++) {
      // Optimal width
      const columnValues = rows.map((row: any) => String(row[i - 1] || ''));

      let maxLength = 10; // minimal width

      columnValues.forEach((value: any) => {
        const cellLength = String(value).length;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });

      // add margin
      worksheet.getColumn(i).width = maxLength + 2;

      // align cells
      worksheet.getColumn(i).alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true,
      };
    }
    // End of header

    worksheet.addRows(rows.slice(1));

    workbook.xlsx.writeBuffer().then((data: Excel.Buffer) => {
      this.saveAsFile(data, `${fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    });
  }

  async saveExcel1(dayData: any[], nightData: any[], periods: string[], fileName: string): Promise<void> {
    try {
      const workbook: Excel.Workbook = new Excel.Workbook();
      // Create one worksheet with both day and night columns
      const rows = this.constructWorkSheet(dayData, nightData, periods);
      const worksheet = workbook.addWorksheet('Planning', {
        properties: { showGridLines: true },
        views: [{ state: 'frozen', ySplit: 1, activeCell: 'A1', showGridLines: true }],
      });
      this.populateWorksheetWithColors(worksheet, rows, dayData, nightData, periods);
      const hasDate = (fileName: string): boolean => {
        return /\d{8}/.test(fileName);
      };
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 10);
      const fullFileName = !hasDate(fileName) ? `${fileName}_${timestamp}.xlsx` : `${fileName}.xlsx`;

      workbook.xlsx.writeBuffer().then((data: Excel.Buffer) => {
        this.save(data, fullFileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      });
    } catch (error) {
      console.error('Error saving Excel file:', error);
      throw error;
    }
  }

  private constructWorkSheet(dayData: any[], nightData: any[], periods: string[]): any[] {
    if ((!dayData || dayData.length === 0) && (!nightData || nightData.length === 0)) {
      return [];
    }

    const hasDayData = periods.includes('DAY');
    const hasNightData = periods.includes('NIGHT');

    const dates: string[] = [];
    const allData = [...(dayData || []), ...(nightData || [])];

    allData.forEach((item: any) => {
      if (item.dates && Array.isArray(item.dates)) {
        item.dates.forEach((d: any) => {
          if (!dates.includes(d.date)) {
            dates.push(d.date);
          }
        });
      }
    });

    dates.sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime());

    // Création de la ligne d'en-tête avec "Nom et Prénom" + dates formatées
    const headerRow = ['Nom et Prénom'];
    dates.forEach((date: any) => {
      const formattedDate = this.formatDate(date);
      if (hasDayData) {
        headerRow.push(`${formattedDate} (Jour)`);
      }
      if (hasNightData) {
        headerRow.push(`${formattedDate} (Nuit)`);
      }
    });

    const allNames = new Set<string>();
    [...(dayData || []), ...(nightData || [])].forEach((item: any) => {
      if (item.fullname !== undefined && item.fullname !== null && item.fullname !== '') {
        allNames.add(item.fullname);
      }
    });

    const dataRows = Array.from(allNames).map((fullname: string) => {
      const row = [fullname];

      const dayPerson = dayData?.find((item: any) => item.fullname === fullname);
      const nightPerson = nightData?.find((item: any) => item.fullname === fullname);

      dates.forEach((date: any) => {
        if (hasDayData) {
          const dayEntry = dayPerson?.dates?.find((d: any) => new Date(d.date).getTime() === new Date(date).getTime());
          const dayCode =
            dayEntry && dayEntry.code !== undefined && dayEntry.code !== null && dayEntry.code !== ''
              ? dayEntry.code +
                (dayEntry.morningStartTime
                  ? ' (' + dayEntry.morningStartTime + '-' + dayEntry.afternoonEndTime + ')'
                  : '')
              : '';
          row.push(dayCode);
        }

        if (hasNightData) {
          const nightEntry = nightPerson?.dates?.find(
            (d: any) => new Date(d.date).getTime() === new Date(date).getTime()
          );
          const nightCode =
            nightEntry && nightEntry.code !== undefined && nightEntry.code !== null && nightEntry.code !== ''
              ? nightEntry.code +
                (nightEntry.nightStartTime
                  ? ' (' + nightEntry.nightStartTime + '-' + nightEntry.nightEndTime + ')'
                  : '')
              : '';
          row.push(nightCode);
        }
      });

      return row;
    });

    return [headerRow, ...dataRows];
  }

  // Peupler la feuille Excel avec les données et appliquer la mise en forme (couleurs des codes horaires, bordures, alignement)
  private populateWorksheetWithColors(
    worksheet: Excel.Worksheet,
    rows: any[],
    dayData: any[],
    nightData: any[],
    periods: string[]
  ): void {
    rows.forEach((row: any) => {
      worksheet.addRow(row);
    });

    const hasDayData = periods.includes('DAY');
    const hasNightData = periods.includes('NIGHT');

    const dates: string[] = [];
    const allData = [...(dayData || []), ...(nightData || [])];

    allData.forEach((item: any) => {
      if (item.dates && Array.isArray(item.dates)) {
        item.dates.forEach((d: any) => {
          if (!dates.includes(d.date)) {
            dates.push(d.date);
          }
        });
      }
    });
    dates.sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime());

    const totalRows = rows.length;
    const columnsPerDate = (hasDayData ? 1 : 0) + (hasNightData ? 1 : 0);
    const totalCols = dates.length * columnsPerDate + 1;

    // Application des bordures à toutes les cellules
    for (let row = 1; row <= totalRows; row++) {
      for (let col = 1; col <= totalCols; col++) {
        const cell = worksheet.getCell(row, col);
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      }
    }
    for (let i = 1; i <= totalCols; i++) {
      const columnValues = rows.map((row: any) => String(row[i - 1] || ''));

      let maxLength = 10;
      columnValues.forEach((value: any) => {
        const cellLength = String(value).length;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });

      worksheet.getColumn(i).width = maxLength + 2;
      worksheet.getColumn(i).alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true,
      };
    }

    const allNames = new Set<string>();
    [...(dayData || []), ...(nightData || [])].forEach((item: any) => {
      if (item.fullname !== undefined && item.fullname !== null && item.fullname !== '') {
        allNames.add(item.fullname);
      }
    });

    Array.from(allNames).forEach((fullname: string, rowIndex: number) => {
      const actualRowIndex = rowIndex + 2;

      const dayPerson = dayData?.find((item: any) => item.fullname === fullname);
      const nightPerson = nightData?.find((item: any) => item.fullname === fullname);

      dates.forEach((date: any, dateIndex: number) => {
        let currentColIndex = dateIndex * columnsPerDate + 2;

        if (hasDayData) {
          const dayEntry = dayPerson?.dates?.find((d: any) => new Date(d.date).getTime() === new Date(date).getTime());

          if (
            dayEntry &&
            dayEntry.color &&
            dayEntry.code !== undefined &&
            dayEntry.code !== null &&
            dayEntry.code !== ''
          ) {
            const cell = worksheet.getCell(actualRowIndex, currentColIndex);
            const hexColor = dayEntry.color.startsWith('#') ? dayEntry.color.substring(1) : dayEntry.color;

            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF' + hexColor },
            };

            const textColor = this.getTextColorForBackground(hexColor);
            cell.font = {
              color: { argb: textColor },
            };
          }
          currentColIndex++;
        }

        if (hasNightData) {
          const nightEntry = nightPerson?.dates?.find(
            (d: any) => new Date(d.date).getTime() === new Date(date).getTime()
          );

          if (
            nightEntry &&
            nightEntry.color &&
            nightEntry.code !== undefined &&
            nightEntry.code !== null &&
            nightEntry.code !== ''
          ) {
            const cell = worksheet.getCell(actualRowIndex, currentColIndex);
            const hexColor = nightEntry.color.startsWith('#') ? nightEntry.color.substring(1) : nightEntry.color;

            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF' + hexColor },
            };

            const textColor = this.getTextColorForBackground(hexColor);
            cell.font = {
              color: { argb: textColor },
            };
          }
        }
      });
    });

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' },
    };
  }

  //Détermine la couleur de texte optimale (noir ou blanc) en fonction de la luminosité de l'arrière-plan
  private getTextColorForBackground(hexColor: string): string {
    hexColor = hexColor.replace('#', '');

    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);

    // Calcul de la luminosité selon la formule de luminance perceptuelle
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Retour blanc si arrière-plan sombre, noir si arrière-plan clair
    return luminance < 0.5 ? 'FFFFFFFF' : 'FF000000';
  }

  private formatDate(date: string): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private save(buffer: Excel.Buffer, fileName: string, mimeType: string): void {
    const blob = new Blob([buffer], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async saveEquityTableExcel(
    columns: { label: string; color?: string }[],
    equityScores: number[],
    profileRows: (string | number)[][],
    fileName: string
  ): Promise<void> {
    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet('Table équité', {
      views: [{ state: 'frozen', ySplit: 2, xSplit: 1, activeCell: 'B3' }],
    });

    worksheet.addRow(columns.map((c) => c.label));
    const headerRow = worksheet.getRow(1);
    headerRow.height = 40;
    columns.forEach((col, i) => {
      const cell = headerRow.getCell(i + 1);
      const argb = col.color ? `FF${col.color.replace('#', '')}` : 'FFD9D9D9';
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
      const textArgb = col.color ? this.getTextColorForBackground(col.color) : 'FF000000';
      cell.font = { bold: true, color: { argb: textArgb } };
      cell.border = {
        bottom: { style: 'medium', color: { argb: 'FF000000' } },
        ...(i >= 2 && { right: { style: 'thin', color: { argb: 'FF808080' } } }),
      };
    });

    // equity score row: first cell is label, then one value per reason-map column, then empty for Total
    worksheet.addRow(["Indice d'équité", '', ...equityScores]);
    const equityRow = worksheet.getRow(2);
    equityRow.font = { bold: true, italic: true };
    equityRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE0CC' } };
    equityRow.alignment = { vertical: 'middle', horizontal: 'center' };
    equityRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };

    profileRows.forEach((row) => {
      const dataRow = worksheet.addRow(row);
      dataRow.alignment = { vertical: 'middle', horizontal: 'center' };
      dataRow.getCell(1).alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column.eachCell?.({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value ? String(cell.value).length : 0;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });
      column.width = Math.min(maxLength + 2, 30);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    this.save(buffer, `${fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }

  async saveExcel2(dayData: any[], fileName: string = 'PSC', addTimestamp: boolean = true): Promise<void> {
    try {
      const workbook: Excel.Workbook = new Excel.Workbook();

      const dayWeeks = this.groupDataByWeeks(dayData);
      // Create sheets for each week
      Object.keys(dayWeeks).forEach((weekKey: any) => {
        const worksheet = this.createWeekSheetWithColors(workbook, dayWeeks[weekKey], 'DAY_NIGHT');
        worksheet.name = `semaine ${weekKey}`;
      });

      let fullFileName: string;

      if (addTimestamp) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 10);
        fullFileName = `${fileName}_${timestamp}.xlsx`;
      } else {
        fullFileName = `${fileName}.xlsx`;
      }

      workbook.xlsx.writeBuffer().then((data: Excel.Buffer) => {
        this.save(data, fullFileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      });
    } catch (error) {
      console.error('Error saving Excel file:', error);
      throw error;
    }
  }

  private getWeekStartDate(date: Date): Date {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }

  private groupDataByWeeks(data: any[]): { [key: string]: any[] } {
    const weeks: { [key: string]: any[] } = {};

    data.forEach((item: any) => {
      if (!item.fullname || item.fullname === null || item.fullname === '') {
        return;
      }

      item.dates.forEach((dateEntry: any) => {
        // Only process entries with defined codes
        if (!dateEntry.code || dateEntry.code === null || dateEntry.code === '') {
          return;
        }

        const date = new Date(dateEntry.date);
        const weekStartDate = this.getWeekStartDate(date);
        const year = weekStartDate.getFullYear();
        const month = String(weekStartDate.getMonth() + 1).padStart(2, '0');
        const day = String(weekStartDate.getDate()).padStart(2, '0');
        const weekKey = `${year}-${month}-${day}`;

        if (!weeks[weekKey]) {
          weeks[weekKey] = [];
        }

        weeks[weekKey].push({
          fullname: item.fullname,
          date: dateEntry.date,
          code: dateEntry.code,
          reason: dateEntry.reason,
          priority: dateEntry.priority,
          morningStartTime: dateEntry.morningStartTime,
          afternoonEndTime: dateEntry.afternoonEndTime,
          nightStartTime: dateEntry.nightStartTime,
          nightEndTime: dateEntry.nightEndTime,
          color: dateEntry.color,
        });
      });
    });

    // trier les codes horaires par priorité
    Object.keys(weeks).forEach((weekKey: any) => {
      weeks[weekKey].sort((a: any, b: any) => (a.priority || 0) - (b.priority || 0));
    });

    return weeks;
  }

  getWeekDates(date: Date, startOnMonday: boolean = true): string[] {
    const day = date.getDay();
    const diff = startOnMonday ? (day === 0 ? -6 : 1 - day) : -day;

    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() + diff);

    const weekDates: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      weekDates.push(d.toISOString().split('T')[0]);
    }

    return weekDates;
  }

  private createWeekSheetWithColors(workbook: Excel.Workbook, weekData: any[], period: string): Excel.Worksheet {
    let uniqueDates: string[] = [];
    if (weekData.length > 0 && weekData[0].date) {
      const oneDate = new Date(weekData[0].date);
      uniqueDates = this.getWeekDates(oneDate, true);
    }

    const codes = [
      ...new Set(
        weekData.map((item: any) => {
          const timeRange = this.getTimeRange(item, period);
          return `${item.code} (${timeRange})`;
        })
      ),
    ];

    const worksheet = workbook.addWorksheet('temp', {
      properties: { showGridLines: true },
      views: [{ state: 'frozen', ySplit: 1, activeCell: 'A1', showGridLines: true }],
    });

    const wsData: any[][] = [];
    const colorData: any[][] = [];

    const headerRow = ['Codes horaires'];
    uniqueDates.forEach((dateStr: any) => {
      const date = new Date(dateStr);
      const dayName = this.getDayName(date);
      const formattedDate = this.formatDateDisplay(date);
      headerRow.push(`${dayName} ${formattedDate}`);
    });
    wsData.push(headerRow);
    colorData.push(new Array(headerRow.length).fill(null)); // No colors for header

    codes.forEach((codeWithTime: any) => {
      let maxProfilesPerDay = 0;
      const codeData: { [key: string]: any[] } = {};

      uniqueDates.forEach((dateStr: any) => {
        const profiles = weekData.filter((item: any) => {
          const itemTimeRange = this.getTimeRange(item, period);
          const itemCodeWithTime = `${item.code} (${itemTimeRange})`;
          return itemCodeWithTime === codeWithTime && this.formatDateForSorting(item.date) === dateStr;
        });

        codeData[dateStr] = profiles;
        maxProfilesPerDay = Math.max(maxProfilesPerDay, profiles.length);
      });

      if (maxProfilesPerDay === 0) {
        const row = [codeWithTime];
        const colorRow = [null];
        uniqueDates.forEach(() => {
          row.push('');
          colorRow.push(null);
        });
        wsData.push(row);
        colorData.push(colorRow);
      } else {
        for (let i = 0; i < maxProfilesPerDay; i++) {
          const row = [i === 0 ? codeWithTime : ''];
          const colorRow = [null];

          uniqueDates.forEach((dateStr: any) => {
            const profiles = codeData[dateStr] || [];
            const profile = profiles[i];

            if (profile) {
              row.push(profile.fullname.toUpperCase());
              colorRow.push(profile.color || null);
            } else {
              row.push('');
              colorRow.push(null);
            }
          });

          wsData.push(row);
          colorData.push(colorRow);
        }
      }
    });

    this.populateWorksheetWithColors2(worksheet, wsData, colorData);

    return worksheet;
  }

  private populateWorksheetWithColors2(worksheet: Excel.Worksheet, rows: any[][], colorData: any[][]): void {
    if (rows.length === 0) {
      return;
    }

    rows.forEach((row: any, index: any) => {
      const excelRow = worksheet.addRow(row);

      if (index === 0) {
        excelRow.font = { bold: true };
        excelRow.alignment = { vertical: 'middle', horizontal: 'center' };
        excelRow.eachCell((cell: any) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE6E6FA' },
          };
        });
      } else {
        excelRow.getCell(1).font = { bold: true };
        excelRow.alignment = { vertical: 'middle', horizontal: 'center' };
      }
    });

    colorData.forEach((colorRow: any[], rowIndex: number) => {
      colorRow.forEach((color: string | null) => {
        if (color && rowIndex > 0) {
          const firstCell = worksheet.getCell(rowIndex + 1, 1);

          let hexColor = color;
          if (hexColor.startsWith('#')) {
            hexColor = hexColor.substring(1);
          }

          // cell.fill = {
          //   type: 'pattern',
          //   pattern: 'solid',
          //   fgColor: { argb: 'FF' + hexColor },
          // };
          // colorer que la premiere colonne des codes horaires
          firstCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF' + hexColor },
          };

          const textColor = this.getTextColorForBackground(hexColor);
          firstCell.font = {
            ...firstCell.font,
            color: { argb: textColor },
          };
        }
      });
    });

    for (let i = 1; i <= rows[0].length; i++) {
      const columnValues = rows.map((row: any) => String(row[i - 1] || ''));

      let maxLength = 10;
      columnValues.forEach((value: any) => {
        const cellLength = String(value).length;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });

      worksheet.getColumn(i).width = Math.min(maxLength + 2, 30);

      worksheet.getColumn(i).alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true,
      };
    }

    for (let i = 1; i <= rows.length; i++) {
      for (let j = 1; j <= rows[0].length; j++) {
        worksheet.getCell(i, j).border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      }
    }
    let currentCode = '';
    let startRow = 2;
    let endRow = 2;

    // merger les cellules ayant le mem code horaire dans la premiere colonne des codes horaires
    for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
      const codeValue = rows[rowIndex][0];

      if (codeValue && codeValue !== '') {
        if (currentCode !== '' && startRow < endRow) {
          worksheet.mergeCells(startRow, 1, endRow, 1);
          worksheet.getCell(startRow, 1).alignment = {
            vertical: 'middle',
            horizontal: 'center',
          };
        }

        currentCode = codeValue;
        startRow = rowIndex + 1;
        endRow = startRow;
      } else {
        endRow = rowIndex + 1;
      }
    }

    if (currentCode !== '' && startRow < endRow) {
      worksheet.mergeCells(startRow, 1, endRow, 1);
      worksheet.getCell(startRow, 1).alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
    }
  }

  private getTimeRange(item: any, period: string): string {
    const isDayNight = period === 'DAY_NIGHT';

    const start = isDayNight
      ? (item.morningStartTime ?? item.nightStartTime)
      : period === 'DAY'
        ? item.morningStartTime
        : item.nightStartTime;

    const end = isDayNight
      ? (item.nightEndTime ?? item.afternoonEndTime)
      : period === 'NIGHT'
        ? item.nightEndTime
        : item.afternoonEndTime;

    return start && end ? `${start}-${end}` : '';
  }

  private getDayName(date: Date): string {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[date.getDay()];
  }

  private formatDateForSorting(date: string): string {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  private formatDateDisplay(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  }

  // private getISOWeek(date: Date): number {
  //   const tempDate = new Date(date.getTime());
  //   const dayNum = (date.getDay() + 6) % 7;
  //   tempDate.setDate(tempDate.getDate() - dayNum + 3);
  //   const firstThursday = tempDate.valueOf();
  //   tempDate.setMonth(0, 1);
  //   if (tempDate.getDay() !== 4) {
  //     tempDate.setMonth(0, 1 + ((4 - tempDate.getDay() + 7) % 7));
  //   }
  //   return 1 + Math.ceil((firstThursday - tempDate.valueOf()) / 604800000);
  // }

  async saveSchedulingPDF(
    schedulingData: any[],
    weekDays: Moment[],
    formationsGroups: Record<string, string[]>,
    roomsName: string,
    displayCodes: boolean,
    fileName: string
  ): Promise<void> {
    try {
      const startDateOfWeek = weekDays[0].format('DD-MM-YYYY');
      const endDateOfWeek = weekDays[weekDays.length - 1].format('DD-MM-YYYY');

      const { jsPDF } = await import('jspdf');
      const pageFormat = weekDays.length <= 5 ? 'a3' : 'a2';
      const doc = new jsPDF({
        orientation: 'l',
        unit: 'mm',
        format: pageFormat,
      });

      const { tableData, headerData, roomSpans, vacationSpans, cellColors, notRooms } = this.constructPDFTableData(
        schedulingData,
        weekDays,
        formationsGroups,
        roomsName,
        displayCodes
      );
      const fontSizeRatio = Math.min(1, 4 / (Object.keys(formationsGroups || {}).length + 2));

      const rowHeights = this.calculateRowHeights(tableData);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pdfWidth = pageWidth - 4;

      const columnStyles = this.generateColumnStyles(weekDays.length, formationsGroups, pdfWidth, fontSizeRatio);
      autoTable(doc, {
        head: headerData.head,
        body: tableData,
        startY: 25,
        pageBreak: 'auto',
        showHead: 'everyPage',
        styles: {
          fontSize: 9 * fontSizeRatio,
          fontStyle: 'bold',
          textColor: [0, 0, 0],
          fillColor: [255, 255, 255],
          lineColor: [0, 0, 0],
          lineWidth: 0.05,
          halign: 'left',
          valign: 'top',
          cellPadding: 1,
          overflow: 'linebreak',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          minCellHeight: 7,
          cellPadding: 1,
          halign: 'left',
          valign: 'top',
        },
        headStyles: {
          fillColor: [200, 230, 255],
          fontStyle: 'bold',
          fontSize: 9 * fontSizeRatio,
          halign: 'center',
          valign: 'middle',
          minCellHeight: 9,
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        },
        columnStyles: {
          0: {
            halign: 'center',
            valign: 'middle',
            cellWidth: 20,
          },
          ...columnStyles.styles,
        },
        margin: { top: 25, left: 2, right: 2, bottom: 4 },
        didDrawPage: (data: any) => {
          const pdfPageWidth = doc.internal.pageSize.getWidth();
          const pageNumber = data.pageNumber;

          doc.setFontSize(16);
          doc.text(`Programme du ${startDateOfWeek} au ${endDateOfWeek}`, pdfPageWidth / 2 - 60, 15);

          const logo = new Image();
          logo.src = 'assets/images/logos/logo_planning.png';
          const qrCode = new Image();
          qrCode.src = 'assets/images/logos/qr-code.png';

          const leftLogoWidth = 40;
          const leftLogoHeight = 15;
          doc.addImage(logo, 'PNG', 12, 5, leftLogoWidth, leftLogoHeight);

          const rightLogoWidth = 18;
          const rightLogoHeight = 18;
          doc.addImage(qrCode, 'PNG', pdfPageWidth - rightLogoWidth - 15, 3, rightLogoWidth, rightLogoHeight);
          if (pageNumber > 1) {
            data.settings.startY = 25;
          }
          // Numéro de page
          doc.setFontSize(9 * fontSizeRatio);
          doc.text(`Page ${pageNumber}`, pdfPageWidth / 2 - 10, doc.internal.pageSize.height - 1);
        },

        didParseCell: (data: any) => {
          const rowIndex = data.row.index;
          const colIndex = data.column.index;
          if (data.cell.text && Array.isArray(data.cell.text)) {
            data.cell.text = data.cell.text.map((line: string) => this.fixEncodingIssues(line));
          } else if (data.cell.text) {
            data.cell.text = this.fixEncodingIssues(data.cell.text.toString());
          }
          // truncate text for formation columns
          if (data.section === 'head') {
            const isFormationColumn = !this.isVacationColumn(colIndex, formationsGroups) && colIndex !== 0;
            const fontSize = 9 * fontSizeRatio;
            const formationColumnWidth = columnStyles.dataColumnWidth;
            if (isFormationColumn && data.cell.text) {
              const maxLength = this.calculateMaxLength(formationColumnWidth, fontSize);
              data.cell.text = this.truncateTextToFit(data.cell.text.toString(), maxLength);
            }
          }
          if (data.section === 'body' && !notRooms.includes(data.row.raw[0])) {
            const isFormationColumn = !this.isVacationColumn(colIndex, formationsGroups) && colIndex !== 0;
            const fontSize = 9 * fontSizeRatio;
            const formationColumnWidth = columnStyles.dataColumnWidth;
            if (isFormationColumn && data.cell.text) {
              const maxLength = this.calculateMaxLength1(formationColumnWidth, fontSize);
              data.cell.text = this.truncateTextToFit1(data.cell.text.toString(), maxLength);
            }
          }

          if (data.section === 'body' && rowHeights[rowIndex]) {
            data.cell.minCellHeight = rowHeights[rowIndex];
          }

          // room merging
          if (colIndex === 0 && data.section === 'body') {
            const roomSpan = roomSpans[rowIndex];
            if (roomSpan && roomSpan.isStart) {
              data.cell.rowSpan = roomSpan.span;
            } else if (roomSpan && !roomSpan.isStart) {
              data.cell.text = '';
            }
          }

          // vacation merging
          if (data.section === 'body' && this.isVacationColumn(colIndex, formationsGroups)) {
            const vacationSpan = vacationSpans[rowIndex];
            if (vacationSpan && vacationSpan.isStart) {
              data.cell.rowSpan = vacationSpan.span;
            } else if (vacationSpan && !vacationSpan.isStart) {
              data.cell.text = '';
            }
          }
        },

        didDrawCell: (data: any) => {
          this.customHeaderStyling(data, doc);
          this.applyCellColors(data, doc, cellColors, roomsName);
        },
      });

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 10);
      const fullFileName = `${fileName}_${timestamp}.pdf`;

      doc.save(fullFileName);
    } catch (error) {
      console.error('Error saving PDF file:', error);
      throw error;
    }
  }

  private fixEncodingIssues(text: string): string {
    if (!text) {
      return '';
    }

    if (!/[ÃƒÂ]/.test(text)) {
      return text;
    }

    const bytes = new Uint8Array(text.split('').map((c: any) => c.charCodeAt(0)));
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  }

  private calculateMaxLength(columnWidth: number, fontSize: number): number {
    const avgCharWidthMm = fontSize * 0.14;

    const cellPadding = 2;
    const availableWidth = columnWidth - cellPadding;

    const maxChars = Math.floor(availableWidth / avgCharWidthMm);

    // leave some buffer space (reduce by 10-15%)
    return Math.max(1, Math.floor(maxChars * 0.85));
  }

  private calculateMaxLength1(columnWidth: number, fontSize: number): number {
    const avgCharWidthMm = fontSize * 0.14;

    const cellPadding = 2;
    const availableWidth = columnWidth - cellPadding;

    const maxChars = Math.floor(availableWidth / avgCharWidthMm);

    // leave some buffer space (reduce by 10-15%)
    return Math.max(1, Math.floor(maxChars * 0.85));
  }

  private truncateTextToFit(text: string, maxLength: number): string {
    if (!text) {
      return '';
    }

    const lines = text.split('\n');
    const truncatedLines = lines.map((line) => {
      if (line.length <= maxLength) {
        return line;
      }
      return line.substring(0, maxLength - 2) + '..';
    });
    return truncatedLines.join('\n');
  }

  private truncateTextToFit1(text: string, maxLength: number): string[] {
    if (!text) {
      return [''];
    }

    const lines = text.split(',');
    if (lines.length > 0) {
      const firstLine = lines[0];

      const match = firstLine.match(/\(.*\)$/);
      const suffix = match ? match[0] : '';
      const mainText = match ? firstLine.slice(0, -suffix.length).trimEnd() : firstLine;

      if (firstLine.length > maxLength) {
        const availableLength = maxLength - suffix.length - 2; // 2 for ".."
        if (availableLength <= 0) {
          lines[0] = '..' + suffix;
        } else {
          lines[0] = mainText.substring(0, availableLength) + '..' + suffix;
        }
      }
    }
    if (lines.length > 1) {
      const secondLine = lines[1];
      if (secondLine.length >= maxLength) {
        lines[1] = secondLine.substring(0, maxLength - 2) + '..';
      }
    }
    return lines;
  }

  private isVacationColumn(colIndex: number, formationsGroups: Record<string, string[]>): boolean {
    if (colIndex === 0) {
      return false;
    }
    const formationsPerDay = Object.keys(formationsGroups || {}).length + 1; // +1 for vacation
    const adjustedIndex = colIndex - 1; // remove room column offset
    return adjustedIndex % formationsPerDay === 0;
  }

  private calculateRowHeights(tableData: any[][]): number[] {
    const rowHeights: number[] = [];
    const baseHeight = 7; // Minimum height
    tableData.forEach((row: any, index: any) => {
      let maxLines = 1;
      row.forEach((cell: any) => {
        if (cell && typeof cell === 'string') {
          const lines = cell.split('\n').length;
          maxLines = Math.max(maxLines, lines);

          const textLength = cell.replace(/\n/g, '').length;
          const estimatedLines = Math.ceil(textLength / 20);
          maxLines = Math.max(maxLines, estimatedLines);
        }
      });

      rowHeights[index] = baseHeight;
    });

    return rowHeights;
  }

  private generateColumnStyles(
    numDays: number,
    formationsGroups: Record<string, string[]>,
    pdfWidth_: number,
    fontSizeRatio: number
  ): any {
    const styles: any = {};
    const pdfWidth = pdfWidth_;
    const roomColumnWidth = 20;
    const availableWidth = pdfWidth - roomColumnWidth;
    const totalDataColumns = numDays * (Object.keys(formationsGroups).length + 1);
    const dataColumnWidth = availableWidth / totalDataColumns + 2 / (Object.keys(formationsGroups).length + 1);
    const vacationsWidth = availableWidth / totalDataColumns - 2;
    for (let day = 0; day < numDays; day++) {
      for (let col = 0; col <= Object.keys(formationsGroups).length; col++) {
        const dayOffset = 1 + day * (Object.keys(formationsGroups).length + 1);
        styles[dayOffset] = {
          cellWidth: vacationsWidth,
          fontSize: 9 * fontSizeRatio,
          cellPadding: 1,
        };
        for (let col = 1; col <= Object.keys(formationsGroups).length; col++) {
          const colIndex = dayOffset + col;
          styles[colIndex] = {
            cellWidth: dataColumnWidth,
            fontSize: 9 * fontSizeRatio,
            cellPadding: 1,
          };
        }
      }
    }

    return { styles: styles, dataColumnWidth: dataColumnWidth };
  }

  private customHeaderStyling(data: any, doc: jsPDF): void {
    if (data.section === 'head' && data.row.index === 0) {
      doc.setFontSize(9);
    }
  }

  //apply cell colors based on specialty
  private applyCellColors(data: any, doc: jsPDF, cellColors: any, roomsName: string): void {
    if (data.row.index === 0 && data.column.index === 0 && data.section === 'head') {
      doc.setFillColor(255, 255, 255);
      doc.rect(data.cell.x - 1, data.cell.y - 1, data.cell.width + 1, data.cell.height + 1, 'F');
      doc.setDrawColor(0, 0, 0);
    }
    if (data.section === 'body' && data.column.index != 0) {
      const rowIndex = data.row.index;
      const colIndex = data.column.index;
      const isRoom = data.row.raw[0].includes(roomsName) && !data.row.raw[0].includes('Hors');

      if (cellColors[rowIndex] && cellColors[rowIndex][colIndex]) {
        const color = cellColors[rowIndex][colIndex];
        const rgb = this.hexToRgb(color);

        if (rgb) {
          doc.setFillColor(rgb.r, rgb.g, rgb.b);
          doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.05);
          doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'S');

          const adaptiveTextColor = this.getTextColorForBackground(color);

          if (adaptiveTextColor === 'FFFFFFFF') {
            doc.setTextColor(255, 255, 255);
          } else {
            doc.setTextColor(0, 0, 0);
          }
          const text = data.cell.text;
          if (text && text.length > 0) {
            if (isRoom) {
              doc.text(text, data.cell.x + 1, data.cell.y + data.cell.height / 4 + 2);
            } else {
              doc.text(text, data.cell.x + 1, data.cell.y + 3);
            }
          }
        }
      } else {
        const rgb = { r: 255, g: 255, b: 255 };
        const color = '#FFFFFF';
        doc.setFillColor(rgb.r, rgb.g, rgb.b);
        doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.05);
        doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'S');

        const adaptiveTextColor = this.getTextColorForBackground(color);

        if (adaptiveTextColor === 'FFFFFFFF') {
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setTextColor(0, 0, 0);
        }
        const text = data.cell.text;
        if (text && text.length > 0) {
          if (isRoom) {
            doc.text(text, data.cell.x + 1, data.cell.y + data.cell.height / 4 + 2);
          } else {
            doc.text(text, data.cell.x + 1, data.cell.y + 3);
          }
        }
      }
    }
  }

  // convert hex color to RGB
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    hex = hex.replace('#', '');

    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char: any) => char + char)
        .join('');
    }

    if (hex.length !== 6) {
      return null;
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return { r, g, b };
  }

  private constructPDFTableData(
    schedulingData: any[],
    weekDays: Moment[],
    formationsGroups: Record<string, string[]>,
    roomsName: string,
    displayCodes: boolean
  ): {
    tableData: any[][];
    headerData: { head: any[][] };
    roomSpans: any;
    vacationSpans: any;
    cellColors: any;
    notRooms: any;
  } {
    const dates = weekDays.map((elt: any) => elt.format('YYYY-MM-DD'));
    dates.sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime());
    const formattedDates = dates.map((d: any) => this.formatDate2(d));

    const headerRow1 = [{ content: '', colSpan: 1, rowSpan: 2, styles: { halign: 'center', valign: 'middle' } }];
    const headerRow2: any[] = [];

    formattedDates.forEach((date: string) => {
      headerRow1.push({
        content: date,
        colSpan: Object.keys(formationsGroups).length + 1,
        rowSpan: 1,
        styles: { halign: 'center', valign: 'middle' },
      });
      headerRow2.push({ content: 'Vacations', styles: { halign: 'center' } });
      for (const [key, _] of Object.entries(formationsGroups)) {
        headerRow2.push({
          content: key.toLowerCase() === 'infirmières' ? 'IBODE/IDE' : key,
          styles: { halign: 'center' },
        });
      }
    });

    const headerData = {
      head: [headerRow1, headerRow2],
    };

    const roomData = this.groupDataByRoom(schedulingData, dates);
    const tableData: any[][] = [];
    const cellColors: any = {};
    const notRooms = Object.keys(roomData).filter(
      (elt: any) => roomData[elt] && roomData[elt].length > 0 && roomData[elt][0].name
    );
    let rowIndex = 0;
    this.sortCustomKeys(Object.keys(roomData), notRooms, roomData).forEach((roomNumber: any) => {
      const roomSlots = roomData[roomNumber];
      const isName = roomData[roomNumber] && roomData[roomNumber].length > 0 ? roomData[roomNumber][0].name : false;
      const timeSlots = this.groupSlotsByTime(roomSlots);

      timeSlots.forEach((timeSlot: any) => {
        let row = [isName ? `${roomNumber}` : `${roomsName} ${roomNumber}`];
        cellColors[rowIndex] = {};

        let colIndex = 1;

        dates.forEach((date: string) => {
          const dateSlots = timeSlot.dateSlots[date] || [];

          if (dateSlots.length > 0) {
            const slot = dateSlots[0];
            const specialtyColor = slot.specialtyColor || null;

            const surgeonInfo = this.formatSurgeonInfo(slot);
            row.push(surgeonInfo);

            if (specialtyColor && surgeonInfo) {
              cellColors[rowIndex][colIndex] = specialtyColor;
            }
            colIndex++;

            const teamByRole = this.organizeTeamByFormation(
              slot.nurses || [],
              slot.anesthetists || [],
              formationsGroups,
              displayCodes,
              !isName
            );
            const teamValues = Object.values(teamByRole) as string[];

            teamValues.forEach((teamValue: string) => {
              row.push(teamValue);
              if (specialtyColor && teamValue && teamValue.trim() !== '') {
                cellColors[rowIndex][colIndex] = specialtyColor;
              }
              colIndex++;
            });
          } else {
            const emptyCells = Array(Object.values(formationsGroups).length + 1).fill('');
            row = [...row, ...emptyCells];
            colIndex += emptyCells.length;
          }
        });

        tableData.push(row);
        rowIndex++;
      });
    });

    const mergedResult = this.mergeArraysByFirstItemWithColors(tableData, cellColors);
    const decomposeTeams = this.splitRowsByLineBreaks(mergedResult.tableData, mergedResult.cellColors, roomsName);

    const dedupedTeams = this.removeDuplicateProfiles(
      decomposeTeams.tableData,
      decomposeTeams.cellColors,
      formationsGroups
    );

    const roomSpans = this.calculateRoomSpans(dedupedTeams.tableData);
    const vacationSpans = this.calculateVacationSpans(dedupedTeams.tableData, formationsGroups);
    return {
      tableData: dedupedTeams.tableData,
      headerData,
      roomSpans,
      vacationSpans,
      cellColors: dedupedTeams.cellColors,
      notRooms: notRooms,
    };
  }

  private sortCustomKeys(keys: string[], specialEndKeys: string[], roomData: any) {
    const lastKey = specialEndKeys.find((e: any) => e.includes('Hors'));
    return keys.sort((a: any, b: any) => {
      const aPriority = roomData[a]?.[0]?.priority ?? 1000;
      const bPriority = roomData[b]?.[0]?.priority ?? 1000;

      if (aPriority === lastKey && bPriority !== lastKey) {
        return 1;
      }
      if (bPriority === lastKey && aPriority !== lastKey) {
        return -1;
      }

      const aIndex = specialEndKeys.indexOf(aPriority);
      const bIndex = specialEndKeys.indexOf(bPriority);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      } else if (aIndex !== -1) {
        return 1;
      } else if (bIndex !== -1) {
        return -1;
      }

      const aNum = Number(aPriority);
      const bNum = Number(bPriority);
      const aIsNum = !isNaN(aNum);
      const bIsNum = !isNaN(bNum);

      if (aIsNum && bIsNum) {
        return aNum - bNum;
      }
      if (aIsNum) {
        return -1;
      }
      if (bIsNum) {
        return 1;
      }

      return aPriority.localeCompare(bPriority);
    });
  }

  private calculateRoomSpans(tableData: any[][]): any {
    const roomSpans: any = {};
    const roomCounts: any = {};

    // Count occurrences of each room
    tableData.forEach((row: any, index: any) => {
      const room = row[0];
      if (!roomCounts[room]) {
        roomCounts[room] = [];
      }
      roomCounts[room].push(index);
    });
    Object.values(roomCounts).forEach((indices: any) => {
      if (indices.length > 1) {
        indices.forEach((index: number, i: number) => {
          roomSpans[index] = {
            span: indices.length,
            isStart: i === 0,
            isEnd: i === indices.length - 1,
          };
        });
      }
    });
    return roomSpans;
  }

  private removeDuplicateProfiles(
    tableData: string[][],
    cellColors: any,
    formationsGroups: Record<string, string[]>
  ): { tableData: string[][]; cellColors: any } {
    const seenProfiles: { [key: string]: Set<string> } = {};

    const cleanedData = tableData.map((row: any, rowIndex: any) => {
      const room = row[0];
      const cleanedRow = [...row];

      const numDays = Math.floor((row.length - 1) / (Object.keys(formationsGroups).length + 1));

      for (let day = 0; day < numDays; day++) {
        const dayOffset = 1 + day * (Object.keys(formationsGroups).length + 1);
        const vacation = row[dayOffset];

        const combinationKey = `${room}|${day}|${vacation}`;

        if (!seenProfiles[combinationKey]) {
          seenProfiles[combinationKey] = new Set<string>();
        }

        for (let col = 1; col <= Object.keys(formationsGroups).length; col++) {
          const colIndex = dayOffset + col;
          const profile = row[colIndex];

          if (profile && profile.trim() !== '') {
            const profileName = profile.split('(')[0].trim();

            if (seenProfiles[combinationKey].has(profileName)) {
              // blank the profile if it's already seen
              cleanedRow[colIndex] = '';

              // Also remove the color for this profile
              if (cellColors[rowIndex] && cellColors[rowIndex][colIndex]) {
                delete cellColors[rowIndex][colIndex];
              }
            } else {
              seenProfiles[combinationKey].add(profileName);
            }
          }
        }
      }

      return cleanedRow;
    });

    return { tableData: cleanedData, cellColors };
  }

  private splitRowsByLineBreaks(
    data: string[][],
    cellColors: any,
    roomsName: string
  ): { tableData: string[][]; cellColors: any } {
    const result: string[][] = [];
    const resultColors: any = {};
    let resultRowIndex = 0;

    for (let originalRowIndex = 0; originalRowIndex < data.length; originalRowIndex++) {
      const row = data[originalRowIndex];
      const isRoom = row[0].includes(roomsName) && !row[0].includes('Hors');
      if (isRoom) {
        // for every cell, split by ##SPLIT##'
        const splitCells = row.map((cell: any) => (cell ? cell.split('##SPLIT##').map((c: any) => c.trim()) : ['']));

        // find the max number of splits in this row
        const maxSplits = Math.max(...splitCells.map((c: any) => c.length));

        //  duplicate the row for each splt index
        for (let i = 0; i < maxSplits; i++) {
          result.push(splitCells.map((cells: any) => (i < cells.length ? cells[i] : cells[0])));

          if (cellColors[originalRowIndex]) {
            resultColors[resultRowIndex] = { ...cellColors[originalRowIndex] };
          }
          resultRowIndex++;
        }
      } else {
        result.push(row);
        if (cellColors[originalRowIndex]) {
          resultColors[resultRowIndex] = { ...cellColors[originalRowIndex] };
        }
        resultRowIndex++;
      }
    }

    return { tableData: result, cellColors: resultColors };
  }

  private calculateVacationSpans(tableData: any[][], formationsGroups: any): any {
    const vacationSpans: any = {};
    const formationsPerDay = Object.keys(formationsGroups || {}).length + 1; // +1 pour vacation

    let i = 0;
    while (i < tableData.length) {
      const row = tableData[i];
      const room = row[0];

      const numDays = Math.floor((row.length - 1) / formationsPerDay);

      let spanCount = 1;
      let j = i + 1;

      while (j < tableData.length && tableData[j][0] === room) {
        let allVacationsMatch = true;

        for (let day = 0; day < numDays; day++) {
          const vacationColIndex = 1 + day * formationsPerDay;
          const currentVacation = row[vacationColIndex];
          const nextVacation = tableData[j][vacationColIndex];

          if (currentVacation !== nextVacation) {
            allVacationsMatch = false;
            break;
          }
        }

        if (allVacationsMatch) {
          spanCount++;
          j++;
        } else {
          break;
        }
      }

      if (spanCount > 1) {
        for (let k = 0; k < spanCount; k++) {
          vacationSpans[i + k] = {
            span: spanCount,
            isStart: k === 0,
            isEnd: k === spanCount - 1,
          };
        }
      }

      i = j;
    }

    return vacationSpans;
  }

  private mergeArraysByFirstItemWithColors(data: any[][], cellColors: any): { tableData: any[][]; cellColors: any } {
    const merged: any[][] = [];
    const mergedColors: any = {};
    let mergedRowIndex = 0;

    for (let i = 0; i < data.length; i++) {
      const arr = data[i];
      const room = arr[0];

      const existingIndex = merged.findIndex((existing: any) => {
        if (existing[0] !== room) {
          return false;
        }
        return !existing.some((val: any, idx: any) => val !== '' && arr[idx] !== '' && idx !== 0);
      });

      if (existingIndex === -1) {
        merged.push([...arr]);
        mergedColors[mergedRowIndex] = { ...cellColors[i] };
        mergedRowIndex++;
      } else {
        merged[existingIndex] = merged[existingIndex].map((val: any, idx: any) => (val !== '' ? val : arr[idx]));

        // Merge colors
        if (cellColors[i]) {
          for (const [colIdx, color] of Object.entries(cellColors[i])) {
            if (!mergedColors[existingIndex][colIdx]) {
              mergedColors[existingIndex][colIdx] = color;
            }
          }
        }
      }
    }

    return { tableData: merged, cellColors: mergedColors };
  }

  private groupDataByRoom(schedulingData: any[], dates: string[]): any {
    const roomData: any = {};

    schedulingData.forEach((dayData: any) => {
      const roomNumber = dayData.room?.roomNumber ? dayData.room?.roomNumber : dayData.room?.name;
      if (!roomNumber) {
        return;
      }

      if (!roomData[roomNumber]) {
        roomData[roomNumber] = [];
      }

      if (dayData.dataByDates && Array.isArray(dayData.dataByDates)) {
        dayData.dataByDates.forEach((dateSlots: any[], dateIndex: number) => {
          const currentDate = dates[dateIndex];
          dateSlots.forEach((slot: any) => {
            roomData[roomNumber].push({
              ...slot,
              date: currentDate,
              room: roomNumber,
              name: dayData.room?.roomNumber ? false : true,
              priority: dayData.room?.priority,
            });
          });
        });
      }
    });

    return roomData;
  }

  private groupSlotsByTime(slots: any[]): any[] {
    const timeGroups: any = {};

    slots.forEach((slot: any) => {
      const timeKey = `${slot.startTime || ''}-${slot.endTime || ''}`;

      if (!timeGroups[timeKey]) {
        timeGroups[timeKey] = {
          startTime: slot.startTime,
          endTime: slot.endTime,
          dateSlots: {},
        };
      }

      if (!timeGroups[timeKey].dateSlots[slot.date]) {
        timeGroups[timeKey].dateSlots[slot.date] = [];
      }

      timeGroups[timeKey].dateSlots[slot.date].push(slot);
    });

    return Object.values(timeGroups);
  }

  private formatSurgeonInfo(slot: any): string {
    const surgeon = slot.surgeonLastName || '';
    const timeRange = slot.startTime && slot.endTime ? `(${slot.startTime}-${slot.endTime})` : '';
    const result = surgeon ? `${surgeon}\n${timeRange}`.trim() : '';
    return result;
  }

  private organizeTeamByFormation(
    nurses: any[],
    anesthesists: any[],
    formationsGroups: Record<string, string[]>,
    displayCodes: boolean,
    isRoom: boolean
  ): any {
    const formationMap = Object.fromEntries(Object.keys(formationsGroups).map((key: any) => [key, '']));
    if ((!nurses || nurses.length === 0) && (!anesthesists || anesthesists.length === 0)) {
      return formationMap;
    }
    const splitText = isRoom ? '##SPLIT##' : '\n';
    if (nurses) {
      nurses.forEach((nurse: any) => {
        const firstName = nurse.profile?.firstName || '';
        const lastName = nurse.profile?.lastName || '';
        const roleName = nurse.role?.diminutif
          ? nurse.role.diminutif
          : nurse.role?.name
            ? nurse.role.name.charAt(0).toUpperCase()
            : '';
        const formationName = nurse.profile?.position || '';
        const endTime = nurse.endTime;
        const startTime = nurse.startTime || '';
        const seniority = nurse.profile?.seniority || '';

        let fullName = roleName
          ? `${firstName.charAt(0).toUpperCase()}. ${lastName} (${roleName})`
          : startTime && endTime
            ? `${firstName.charAt(0).toUpperCase()}. ${lastName} (${startTime} - ${endTime})`
            : seniority
              ? `${firstName.charAt(0).toUpperCase()}. ${lastName} (${seniority.slice(0, 3)})`
              : `${firstName.charAt(0).toUpperCase()}. ${lastName}`;

        if (displayCodes && nurse.code) {
          fullName += nurse.morningStartTime
            ? `\n${nurse.code}|${nurse.morningStartTime}-${nurse.afternoonEndTime}`
            : `\n${nurse.code}|${nurse.nightStartTime}-${nurse.nightEndTime}`;
        }
        let key = '';
        for (const [groupKey, groupValues] of Object.entries(formationsGroups)) {
          if ((groupValues as string[]).includes(formationName)) {
            key = groupKey;
            break;
          }
        }

        if (key && formationMap.hasOwnProperty(key)) {
          if (formationMap[key]) {
            formationMap[key] += `${splitText}${fullName}`;
          } else {
            formationMap[key] = fullName;
          }
        }
      });
    }
    if (anesthesists) {
      anesthesists.forEach((anesth: any) => {
        const firstName = anesth.firstName || '';
        const lastName = anesth.lastName || '';
        const formationName = anesth.position || '';
        const seniority = anesth.seniority || '';
        let fullName = seniority
          ? `${firstName.charAt(0).toUpperCase()}. ${lastName} (${seniority.slice(0, 3)})`
          : `${firstName.charAt(0).toUpperCase()}. ${lastName}`;
        if (displayCodes && anesth.code) {
          fullName += anesth.morningStartTime
            ? `\n${anesth.code}|${anesth.morningStartTime}-${anesth.afternoonEndTime}`
            : `\n${anesth.code}|${anesth.nightStartTime}-${anesth.nightEndTime}`;
        }
        let key = '';
        for (const [groupKey, groupValues] of Object.entries(formationsGroups)) {
          if ((groupValues as string[]).includes(formationName)) {
            key = groupKey;
            break;
          }
        }

        if (key && formationMap.hasOwnProperty(key)) {
          if (formationMap[key]) {
            formationMap[key] += `${splitText}${fullName}`;
          } else {
            formationMap[key] = fullName;
          }
        }
      });
    }
    return formationMap;
  }

  private formatDate2(date: string): string {
    const d = new Date(date);

    const weekdays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

    const dayName = weekdays[d.getDay()];

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');

    return `${dayName} ${day}/${month}`;
  }

  async saveDailySchedulingPDF(
    dailyData: any[],
    day: moment.Moment,
    roomsName: string,
    fileName: string,
    showComments: boolean = true,
    intervenantColumns: {
      groupId: string;
      groupName: string;
      header: string;
      formationIds: string[];
    }[],
    selectedServices: string = ''
  ): Promise<void> {
    try {
      const currentDate = day.format('DD-MM-YYYY');

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({
        orientation: 'l',
        unit: 'mm',
        format: 'a3',
        compress: true,
      });

      const { tableData, roomSpans, cellColors, footerStartIndex, headRow } = this.constructDailyPDFTableData(
        dailyData,
        showComments,
        intervenantColumns,
        roomsName
      );

      const fontSizeRatio = 1;
      const minFooterFontSize = 10 * fontSizeRatio;
      let uniformFooterFontSize = minFooterFontSize;
      let uniformFooterFontSizeTwoCol = minFooterFontSize;

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pdfWidth = pageWidth - 4;

      const firstColWidth = 25;
      const remainingWidth = pdfWidth - firstColWidth;

      const withComments = !!showComments;
      const dynamicCount = intervenantColumns.length;

      //column widths for 1-column layout
      const vacationWidth = remainingWidth * 0.18;
      const remainingAfterVacation = remainingWidth - vacationWidth;

      //remaining columns: intervenants + comment
      const otherColsCount = dynamicCount + (withComments ? 1 : 0);
      const otherWidth = otherColsCount > 0 ? remainingAfterVacation / otherColsCount : 0;

      //header for one column
      const headOneCol = [headRow];

      const columnStylesOneCol: any = {
        0: { halign: 'center', valign: 'middle', cellWidth: firstColWidth },
        1: { halign: 'left', valign: 'middle', cellWidth: vacationWidth },
      };

      //dynamic column
      for (let i = 0; i < dynamicCount; i++) {
        const colIndex = 2 + i;
        columnStylesOneCol[colIndex] = {
          halign: 'left',
          valign: 'middle',
          cellWidth: otherWidth,
        };
      }
      //comment column (if enabled)
      if (withComments) {
        const commentColIndex = 2 + dynamicCount;
        columnStylesOneCol[commentColIndex] = {
          halign: 'left',
          valign: 'middle',
          cellWidth: otherWidth,
        };
      }

      const tableBody = tableData;

      const computeNeededHeightForText = (text: string, usableWidth: number, fontSize: number): number => {
        const padY = 2;
        doc.setFontSize(fontSize);

        const lines = doc.splitTextToSize((text || '').trim(), Math.max(1, usableWidth)) as string[];
        const lineHeight = fontSize * 0.35;

        return Math.max(7, padY * 2 + lines.length * lineHeight + 2);
      };

      const computeNeededHeightForFooterLabel = (label: string, labelWidth: number): number => {
        const padY = 2;
        const fontSize = 10;

        doc.setFontSize(fontSize);

        const usable = Math.max(1, labelWidth - 2 * 1.5);
        const lines = doc.splitTextToSize((label || '').trim(), usable) as string[];
        const lineHeight = fontSize * 0.35;

        return Math.max(7, padY * 2 + lines.length * lineHeight + 2);
      };

      let rowHeightsOneCol = this.calculateDailyRowHeights(tableBody);

      const commentColIdx = withComments ? 2 + dynamicCount : -1;

      if (commentColIdx !== -1) {
        const colW = columnStylesOneCol[commentColIdx].cellWidth as number;

        for (let i = 0; i < footerStartIndex; i++) {
          const raw = (tableBody[i]?.[commentColIdx] ?? '').toString();
          if (!raw.trim()) {
            continue;
          }

          const padX = 1;
          const padY = 2;

          const usableW = Math.max(1, colW - padX * 2);

          const fs = 13 * fontSizeRatio;
          doc.setFontSize(fs);

          let needed = 7;

          if (this.looksLikeHtml(raw)) {
            const tokens = this.htmlToTokens(raw);

            let plain = '';
            for (const t of tokens) {
              if (t.type === 'newline') {
                plain += '\n';
              } else {
                plain += t.text;
              }
            }
            plain = plain.replace(/\n{3,}/g, '\n\n').trim();

            const lines = doc.splitTextToSize(plain, usableW) as string[];
            const lineH = fs * 0.35;
            needed = padY * 4 + lines.length * lineH;
          } else {
            const lines = doc.splitTextToSize(raw.trim(), usableW) as string[];
            const lineH = fs * 0.35;
            needed = padY * 4 + lines.length * lineH;
          }

          rowHeightsOneCol[i] = Math.max(rowHeightsOneCol[i] ?? 0, needed);
        }
      }

      const footerContentUsableWidthOneCol = pdfWidth - firstColWidth - 2 * 1.5;

      for (let i = footerStartIndex; i < tableBody.length; i++) {
        const row = tableBody[i];

        const isFooterRow = row?.length >= 2 && typeof row[1] === 'object' && row[1] !== null && 'colSpan' in row[1];
        if (!isFooterRow) {
          continue;
        }

        const label = typeof row[0] === 'string' ? row[0] : '';
        const neededLabel = computeNeededHeightForFooterLabel(label, firstColWidth);

        const cellObj = row[1];
        const text = typeof cellObj?.content === 'string' ? cellObj.content : '';
        if (text && this.looksLikeHtml(text)) {
          const tokens = this.htmlToTokens(text);

          const textLines = tokens.filter((t) => t.type === 'newline').length + 1;

          const hasTags = this.extractTagsFromText(text).tags.length > 0;
          const tagLines = hasTags ? 1 : 0;

          const lineHeight = 7.2 * fontSizeRatio;
          const padY = 1.5;

          const totalLines = textLines + tagLines + 1;

          const neededHtml = padY * 2 + totalLines * lineHeight;

          rowHeightsOneCol[i] = Math.max(rowHeightsOneCol[i] ?? 0, neededHtml);
          continue;
        }

        const needed = computeNeededHeightForText(text, footerContentUsableWidthOneCol, minFooterFontSize);
        rowHeightsOneCol[i] = Math.max(rowHeightsOneCol[i] ?? 0, neededLabel, needed);
      }

      const headerHeight = 9;
      const usableHeight = pageHeight - 25 - 12;

      const roomsRowHeights = rowHeightsOneCol.slice(0, footerStartIndex);
      const footerRowHeights = rowHeightsOneCol.slice(footerStartIndex);

      const totalRoomsHeight = roomsRowHeights.reduce((a, b) => a + b, 0);
      const totalFooterHeight = footerRowHeights.reduce((a, b) => a + b, 0);

      const totalHeightOneCol = headerHeight + totalRoomsHeight + totalFooterHeight;

      //stretch rows vertically to occupy all available height using 1 column layout
      if (totalHeightOneCol < usableHeight) {
        const extraSpace = usableHeight - totalHeightOneCol;

        const roomsCount = footerStartIndex;

        if (roomsCount > 0) {
          const delta = extraSpace / roomsCount;

          rowHeightsOneCol = rowHeightsOneCol.map((h, idx) => {
            if (idx < footerStartIndex) {
              return h + delta;
            }
            return h;
          });
        }
      }
      //common page header
      const didDrawPageCommon = (data: any) => {
        const pdfPageWidth = doc.internal.pageSize.getWidth();
        const pageNumber = data.pageNumber;

        const formatDateWithDay = (dateString: string): string => {
          const [day, month, year] = dateString.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

          const daysOfWeek = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

          const dayOfWeek = daysOfWeek[date.getDay()];
          return `${dayOfWeek} ${dateString}`;
        };

        doc.setFontSize(16);
        let title = `Répartition du ${formatDateWithDay(currentDate)}`;

        if (selectedServices && selectedServices.trim() !== '') {
          title += ` - ${selectedServices}`;
        }

        const titleWidth = doc.getTextWidth(title);
        const titleX = (pdfPageWidth - titleWidth) / 2;

        doc.text(title, titleX, 15);

        const logo = new Image();
        logo.src = 'assets/images/logos/logo_planning.png';
        const qrCode = new Image();
        qrCode.src = 'assets/images/logos/qr-code.png';

        const leftLogoWidth = 40;
        const leftLogoHeight = 15;
        doc.addImage(logo, 'PNG', 12, 5, leftLogoWidth, leftLogoHeight);

        const rightLogoWidth = 18;
        const rightLogoHeight = 18;
        doc.addImage(qrCode, 'PNG', pdfPageWidth - rightLogoWidth - 15, 3, rightLogoHeight, rightLogoHeight);

        if (pageNumber > 1) {
          data.settings.startY = 25;
        }

        doc.setFontSize(9 * fontSizeRatio);
        doc.text(`Page ${pageNumber}`, pdfPageWidth / 2 - 10, doc.internal.pageSize.height - 1);
      };

      const createDidParseCell = (
        rowHeightsLocal: number[],
        roomSpansLocal: { [rowIndex: number]: { span: number; isStart: boolean } },
        enableRowSpan: boolean,
        roomsRowsCount: number
      ) => {
        return (data: any) => {
          const rowIndex = data.row.index;
          const colIndex = data.column.index;
          const isRoomRow = rowIndex < roomsRowsCount;

          if (data.cell.text && Array.isArray(data.cell.text)) {
            data.cell.text = data.cell.text.map((line: string) => this.fixEncodingIssues(line));
          } else if (data.cell.text) {
            data.cell.text = this.fixEncodingIssues(data.cell.text.toString());
          }

          if (data.section === 'body' && rowHeightsLocal[rowIndex]) {
            const h = rowHeightsLocal[rowIndex];
            data.cell.minCellHeight = h;
            data.row.height = h;
          }

          if (data.section !== 'body') {
            return;
          }

          if (enableRowSpan && isRoomRow && colIndex === 0) {
            const span = roomSpansLocal[rowIndex];

            if (span && !span.isStart) {
              data.cell.text = '';
              return;
            }

            const originalText = Array.isArray(data.cell.text)
              ? data.cell.text.join('\n')
              : (data.cell.text ?? '').toString();

            (data.cell as any).__cellText = originalText;
            data.cell.text = '';

            if (span && span.isStart) {
              data.cell.rowSpan = span.span;
            }

            return;
          }

          const originalText = Array.isArray(data.cell.text)
            ? data.cell.text.join('\n')
            : (data.cell.text ?? '').toString();

          (data.cell as any).__cellText = originalText;
          data.cell.text = '';
        };
      };

      const createDidParseCellTwoCols = (
        rowHeightsLocal: number[],
        roomSpansLeftLocal: { [rowIndex: number]: { span: number; isStart: boolean } },
        roomSpansRightLocal: { [rowIndex: number]: { span: number; isStart: boolean } },
        rightSalleColIndex: number,
        roomsRowsCount: number
      ) => {
        return (data: any) => {
          const rowIndex = data.row.index;
          const colIndex = data.column.index;
          const isFooterRow = rowIndex >= roomsRowsCount;

          if (data.cell.text && Array.isArray(data.cell.text)) {
            data.cell.text = data.cell.text.map((line: string) => this.fixEncodingIssues(line));
          } else if (data.cell.text) {
            data.cell.text = this.fixEncodingIssues(data.cell.text.toString());
          }

          if (data.section === 'body' && rowHeightsLocal[rowIndex]) {
            const h = rowHeightsLocal[rowIndex];
            data.cell.minCellHeight = h;
            if (!data.row.height || data.row.height < h) {
              data.row.height = h;
            }
          }

          if (data.section !== 'body') {
            return;
          }

          if (!isFooterRow) {
            if (colIndex === 0) {
              const span = roomSpansLeftLocal[rowIndex];

              if (span && !span.isStart) {
                data.cell.text = '';
                return;
              }

              const originalText = Array.isArray(data.cell.text)
                ? data.cell.text.join('\n')
                : (data.cell.text ?? '').toString();

              (data.cell as any).__cellText = originalText;
              data.cell.text = '';

              if (span && span.isStart) {
                data.cell.rowSpan = span.span;
              }

              return;
            }

            if (colIndex === rightSalleColIndex) {
              const span = roomSpansRightLocal[rowIndex];

              if (span && !span.isStart) {
                data.cell.text = '';
                return;
              }

              const originalText = Array.isArray(data.cell.text)
                ? data.cell.text.join('\n')
                : (data.cell.text ?? '').toString();

              (data.cell as any).__cellText = originalText;
              data.cell.text = '';

              if (span && span.isStart) {
                data.cell.rowSpan = span.span;
              }

              return;
            }
          }

          const originalText = Array.isArray(data.cell.text)
            ? data.cell.text.join('\n')
            : (data.cell.text ?? '').toString();

          (data.cell as any).__cellText = originalText;
          data.cell.text = '';
        };
      };

      const didDrawCellCommon = (
        cellColorsLocal: { [rowIndex: number]: { [colIndex: number]: string } },
        bodyLength: number
      ) => {
        return (data: any) => {
          this.customHeaderStyling(data, doc);
          if (data.section === 'body' && data.row.index < bodyLength) {
            this.applyCellColors(data, doc, cellColorsLocal, roomsName);
          }
        };
      };

      const didDrawCellOneCol = (
        cellColorsLocal: { [rowIndex: number]: { [colIndex: number]: string } },
        roomsRowsCount: number,
        doc: jsPDF,
        roomSpansLocal: { [rowIndex: number]: { span: number; isStart: boolean } },
        fontSizeRatioLocal: number
      ) => {
        const baseHandler = didDrawCellCommon(cellColorsLocal, roomsRowsCount);

        return (data: any) => {
          baseHandler(data);

          if (data.section !== 'body') {
            return;
          }

          const rowIndex = data.row.index;
          const colIndex = data.column.index;

          if (rowIndex < roomsRowsCount && colIndex === 0) {
            const span = roomSpansLocal[rowIndex];
            if (span && !span.isStart) {
              return;
            }
          }

          const rawText = (data.cell as any).__cellText as string | undefined;
          if (!rawText) {
            return;
          }

          const cell = data.cell;

          if (this.looksLikeHtml(rawText)) {
            this.drawHtmlInCell(doc, cell, rawText, fontSizeRatioLocal);
            return;
          }

          const halign = (cell.styles?.halign || 'left') as 'left' | 'center' | 'right';
          const isFooterTable = roomsRowsCount === 0;

          if (isFooterTable) {
            this.drawNameTimeWithScaling(doc, cell, rawText, halign, false, uniformFooterFontSize, minFooterFontSize);
            return;
          }
          this.drawNameTimeWithScaling(doc, cell, rawText, halign, false);
        };
      };

      const didDrawCellTwoCols = (
        cellColorsLocal: { [rowIndex: number]: { [colIndex: number]: string } },
        roomsRowsCount: number,
        doc: jsPDF,
        roomSpansLeftLocal: { [rowIndex: number]: { span: number; isStart: boolean } },
        roomSpansRightLocal: { [rowIndex: number]: { span: number; isStart: boolean } },
        rightSalleColIndex: number,
        fontSizeRatioLocal: number
      ) => {
        const baseHandler = didDrawCellCommon(cellColorsLocal, roomsRowsCount);
        const defaultLineWidth = 0.05;

        let dividerX: number;
        let dividerYTop: number;
        let dividerYBottom: number;

        return (data: any) => {
          baseHandler(data);

          const section = data.section;
          const rowIndex = data.row.index;
          const colIndex = data.column.index;
          const cell = data.cell;

          if (section === 'head' && colIndex === rightSalleColIndex) {
            dividerX = cell.x;
            dividerYTop = cell.y;
            dividerYBottom = cell.y + cell.height;
          }

          if (section === 'body' && colIndex === rightSalleColIndex && rowIndex < roomsRowsCount) {
            if (dividerX === null) {
              dividerX = cell.x;
            }
            if (dividerYTop === null || cell.y < dividerYTop) {
              dividerYTop = cell.y;
            }

            const bottom = cell.y + cell.height;
            if (dividerYBottom === null || bottom > dividerYBottom) {
              dividerYBottom = bottom;
            }

            if (
              rowIndex === roomsRowsCount - 1 &&
              dividerX !== null &&
              dividerYTop !== null &&
              dividerYBottom !== null
            ) {
              doc.setDrawColor(0, 0, 0);
              doc.setLineWidth(0.8);
              doc.line(dividerX, dividerYTop, dividerX, dividerYBottom);
              doc.setLineWidth(defaultLineWidth);
            }
          }

          if (section !== 'body') {
            return;
          }

          // rowSpan
          if (rowIndex < roomsRowsCount) {
            if (colIndex === 0) {
              const span = roomSpansLeftLocal[rowIndex];
              if (span && !span.isStart) {
                return;
              }
            } else if (colIndex === rightSalleColIndex) {
              const span = roomSpansRightLocal[rowIndex];
              if (span && !span.isStart) {
                return;
              }
            }
          }

          const rawText = (cell as any).__cellText as string | undefined;
          if (!rawText) {
            return;
          }

          if (this.looksLikeHtml(rawText)) {
            this.drawHtmlInCell(doc, cell, rawText, fontSizeRatioLocal);
            return;
          }

          const halign = (cell.styles?.halign || 'left') as 'left' | 'center' | 'right';
          const isFooterTable = roomsRowsCount === 0;

          this.drawNameTimeWithScaling(
            doc,
            cell,
            rawText,
            halign,
            true,
            undefined,
            isFooterTable && rawText && rawText.trim().toLowerCase() === 'commentaire' ? 6 : minFooterFontSize
          );
        };
      };

      // 1 column mode
      if (totalHeightOneCol <= usableHeight) {
        const roomsBody = tableBody.slice(0, footerStartIndex);
        const footerBody = tableBody.slice(footerStartIndex);

        const roomsHeights = rowHeightsOneCol.slice(0, footerStartIndex);
        const footerHeights = rowHeightsOneCol.slice(footerStartIndex);
        uniformFooterFontSize = this.computeUniformFooterFontSize(
          doc,
          footerBody,
          footerHeights,
          footerContentUsableWidthOneCol,
          minFooterFontSize
        );
        autoTable(doc as any, {
          head: headOneCol,
          body: roomsBody,
          startY: 25,
          pageBreak: 'auto',
          showHead: 'everyPage',
          rowPageBreak: 'avoid',
          styles: {
            fontSize: 9 * fontSizeRatio,
            fontStyle: 'normal',
            textColor: [0, 0, 0],
            fillColor: [255, 255, 255],
            lineColor: [0, 0, 0],
            lineWidth: 0.05,
            halign: 'left',
            valign: 'middle',
            cellPadding: { top: 2, right: 1, bottom: 2, left: 1 },
            overflow: 'linebreak',
            cellWidth: 'wrap',
          },
          bodyStyles: {
            minCellHeight: 10,
            fontStyle: 'normal',
            cellPadding: { top: 2, right: 1, bottom: 2, left: 1 },
            halign: 'left',
            valign: 'middle',
          },
          headStyles: {
            fillColor: [200, 230, 255],
            fontStyle: 'bold',
            fontSize: 13 * fontSizeRatio,
            halign: 'center',
            valign: 'middle',
            minCellHeight: 10,
            cellPadding: { top: 3, right: 1, bottom: 3, left: 1 },
          },
          columnStyles: columnStylesOneCol,
          margin: { top: 25, left: 2, right: 2, bottom: 4 },
          didDrawPage: didDrawPageCommon,
          didParseCell: createDidParseCell(roomsHeights, roomSpans, true, roomsBody.length),
          didDrawCell: didDrawCellOneCol(cellColors, roomsBody.length, doc, roomSpans, fontSizeRatio),
        });
        if (footerBody.length > 0) {
          const startYFooter = ((doc as any).lastAutoTable?.finalY ?? 25) + 1;

          autoTable(doc as any, {
            head: [], // pas de header
            body: footerBody,
            startY: startYFooter,
            pageBreak: 'auto',
            showHead: 'never',
            rowPageBreak: 'avoid',
            styles: {
              fontSize: 9 * fontSizeRatio,
              fontStyle: 'normal',
              textColor: [0, 0, 0],
              fillColor: [255, 255, 255],
              lineColor: [0, 0, 0],
              lineWidth: 0.05,
              halign: 'left',
              valign: 'middle',
              cellPadding: { top: 2, right: 1, bottom: 2, left: 1 },
              overflow: 'linebreak',
              cellWidth: 'wrap',
            },
            bodyStyles: {
              minCellHeight: 7,
              fontStyle: 'normal',
              cellPadding: { top: 2, right: 1, bottom: 2, left: 1 },
              halign: 'left',
              valign: 'middle',
            },
            columnStyles: columnStylesOneCol,
            margin: { top: 25, left: 2, right: 2, bottom: 4 },
            didDrawPage: didDrawPageCommon,

            didParseCell: createDidParseCell(footerHeights, {}, false, 0),

            didDrawCell: didDrawCellOneCol({}, 0, doc, {}, fontSizeRatio),
          });
        }
      } else {
        //2 columns mode
        const roomsBody = tableBody.slice(0, footerStartIndex);
        const footerBodyOriginal = tableBody.slice(footerStartIndex);

        type Group = {
          rows: any[][];
          heights: number[];
          indexes: number[];
        };

        const groups: Group[] = [];
        let currentGroup: Group | null = null;
        let currentSalle = '';
        //build groups per room
        for (let i = 0; i < roomsBody.length; i++) {
          const row = roomsBody[i];
          const salleName = row[0] || '';

          if (!currentGroup || salleName !== currentSalle) {
            currentGroup = { rows: [], heights: [], indexes: [] };
            groups.push(currentGroup);
            currentSalle = salleName;
          }

          currentGroup.rows.push(row);
          currentGroup.heights.push(roomsRowHeights[i]);
          currentGroup.indexes.push(i);
        }
        //distribute groups between left and right column
        const leftGroups: Group[] = [];
        const rightGroups: Group[] = [];

        const totalRows = groups.reduce((acc, g) => acc + g.rows.length, 0);
        const targetLeftRows = Math.ceil(totalRows / 2);

        let leftRowsCount = 0;

        for (const g of groups) {
          const gRows = g.rows.length;

          if (leftGroups.length === 0 || leftRowsCount + gRows <= targetLeftRows) {
            leftGroups.push(g);
            leftRowsCount += gRows;
          } else {
            rightGroups.push(g);
          }
        }

        if (rightGroups.length === 0 && leftGroups.length > 1) {
          rightGroups.push(leftGroups.pop() as Group);
        }

        const leftRows: any[][] = [];
        const rightRows: any[][] = [];
        const roomSpansLeft: { [rowIndex: number]: { span: number; isStart: boolean } } = {};
        const roomSpansRight: { [rowIndex: number]: { span: number; isStart: boolean } } = {};
        const leftIndexMap: number[] = [];
        const rightIndexMap: number[] = [];

        const pushGroupToSide = (
          sideRows: any[][],
          sideSpans: { [rowIndex: number]: { span: number; isStart: boolean } },
          sideIndexMap: number[],
          group: Group
        ) => {
          const span = group.rows.length;
          group.rows.forEach((row, idx) => {
            const rowIndex = sideRows.length;
            sideRows.push(row);
            sideIndexMap[rowIndex] = group.indexes[idx];
            if (span > 1) {
              sideSpans[rowIndex] = { span, isStart: idx === 0 };
            }
          });
        };

        for (const g of leftGroups) {
          pushGroupToSide(leftRows, roomSpansLeft, leftIndexMap, g);
        }
        for (const g of rightGroups) {
          pushGroupToSide(rightRows, roomSpansRight, rightIndexMap, g);
        }

        //number of columns for one block
        const blockColsCount = 2 + dynamicCount + (withComments ? 1 : 0);

        const makeEmptyRoomRow = (): any[] => Array(blockColsCount).fill('');

        const twoColBodyRooms: any[][] = [];
        const maxRows = Math.max(leftRows.length, rightRows.length);
        const cellColorsTwoCol: { [rowIndex: number]: { [colIndex: number]: string } } = {};

        for (let i = 0; i < maxRows; i++) {
          const leftRow = leftRows[i] !== undefined ? leftRows[i] : makeEmptyRoomRow();
          const rightRow = rightRows[i] !== undefined ? rightRows[i] : makeEmptyRoomRow();
          const mergedRow = [...leftRow, ...rightRow];
          const newRowIndex = twoColBodyRooms.length;
          twoColBodyRooms.push(mergedRow);

          const colorRow: { [colIndex: number]: string } = {};
          const originalLeftIdx = leftIndexMap[i];
          if (originalLeftIdx != null && cellColors[originalLeftIdx] && cellColors[originalLeftIdx][1]) {
            colorRow[1] = cellColors[originalLeftIdx][1];
          }

          const originalRightIdx = rightIndexMap[i];
          if (originalRightIdx != null && cellColors[originalRightIdx] && cellColors[originalRightIdx][1]) {
            const vacationRightColIndex = blockColsCount + 1;
            colorRow[vacationRightColIndex] = cellColors[originalRightIdx][1];
          }

          if (Object.keys(colorRow).length > 0) {
            cellColorsTwoCol[newRowIndex] = colorRow;
          }
        }

        const roomsHeader = roomsName && roomsName.trim().length > 0 ? roomsName.trim() : 'Salle';

        const leftHeaders = [
          roomsHeader,
          'Vacations',
          ...intervenantColumns.map((c) => c.header),
          ...(withComments ? ['Commentaire'] : []),
        ];

        const rightHeaders = [
          roomsHeader,
          'Vacations',
          ...intervenantColumns.map((c) => c.header),
          ...(withComments ? ['Commentaire'] : []),
        ];

        const headTwoCol = [[...leftHeaders, ...rightHeaders]];
        const rightSalleColIndex = blockColsCount;

        // column widths for 2 columns layout
        const halfWidth = pdfWidth / 2;

        const firstColLeft2 = 25;
        const firstColRight2 = 25;

        const remainingLeft2 = halfWidth - firstColLeft2;
        const remainingRight2 = halfWidth - firstColRight2;

        const vacationWidthLeft2 = remainingLeft2 * 0.18;
        const vacationWidthRight2 = remainingRight2 * 0.18;

        const remainingLeftAfterVac = remainingLeft2 - vacationWidthLeft2;
        const remainingRightAfterVac = remainingRight2 - vacationWidthRight2;

        const otherColsBlock = dynamicCount + (withComments ? 1 : 0);
        const otherWidthLeft2 = otherColsBlock > 0 ? remainingLeftAfterVac / otherColsBlock : 0;
        const otherWidthRight2 = otherColsBlock > 0 ? remainingRightAfterVac / otherColsBlock : 0;

        const columnStylesTwoCol: any = {};

        //left block
        columnStylesTwoCol[0] = { halign: 'center', valign: 'middle', cellWidth: firstColLeft2 }; // Salle gauche
        columnStylesTwoCol[1] = { halign: 'left', valign: 'middle', cellWidth: vacationWidthLeft2 }; // Vacation gauche

        for (let i = 0; i < dynamicCount; i++) {
          const colIndex = 2 + i;
          columnStylesTwoCol[colIndex] = {
            halign: 'left',
            valign: 'middle',
            cellWidth: otherWidthLeft2,
          };
        }

        if (withComments) {
          const commentLeftIndex = 2 + dynamicCount;
          columnStylesTwoCol[commentLeftIndex] = {
            halign: 'left',
            valign: 'middle',
            cellWidth: otherWidthLeft2,
          };
        }

        //right block
        columnStylesTwoCol[rightSalleColIndex] = {
          halign: 'center',
          valign: 'middle',
          cellWidth: firstColRight2,
        };
        columnStylesTwoCol[rightSalleColIndex + 1] = {
          halign: 'left',
          valign: 'middle',
          cellWidth: vacationWidthRight2,
        };

        for (let i = 0; i < dynamicCount; i++) {
          const colIndex = rightSalleColIndex + 2 + i;
          columnStylesTwoCol[colIndex] = {
            halign: 'left',
            valign: 'middle',
            cellWidth: otherWidthRight2,
          };
        }

        if (withComments) {
          const commentRightIndex = rightSalleColIndex + 2 + dynamicCount;
          columnStylesTwoCol[commentRightIndex] = {
            halign: 'left',
            valign: 'middle',
            cellWidth: otherWidthRight2,
          };
        }

        //footer in 2 columns
        const footerBody = footerBodyOriginal.map((row: any[]) => {
          if (row.length >= 2 && typeof row[1] === 'object') {
            const cell = { ...(row[1] as any) };
            cell.colSpan = headTwoCol[0].length - 1;
            return [row[0], cell];
          }
          return row;
        });

        const twoColFullBody = [...twoColBodyRooms, ...footerBody];
        const roomsRowsCount = twoColBodyRooms.length;

        let rowHeightsTwoCol = this.calculateDailyRowHeights(twoColFullBody);

        if (withComments) {
          const commentLeftIdx = 2 + dynamicCount;
          const commentRightIdx = rightSalleColIndex + 2 + dynamicCount;

          const colWLeft = columnStylesTwoCol[commentLeftIdx].cellWidth as number;
          const colWRight = columnStylesTwoCol[commentRightIdx].cellWidth as number;

          const padX = 1;
          const padY = 2;

          const fs = 13 * fontSizeRatio;
          doc.setFontSize(fs);

          const computeNeededFromRaw = (raw: string, colW: number): number => {
            const usableW = Math.max(1, colW - padX * 2);
            const fs = 13 * fontSizeRatio;

            doc.setFontSize(fs);

            if (this.looksLikeHtml(raw)) {
              const tokens = this.htmlToTokens(raw);
              let plain = '';
              for (const t of tokens) {
                if (t.type === 'newline') {
                  plain += '\n';
                } else {
                  plain += t.text;
                }
              }
              plain = plain.replace(/\n{3,}/g, '\n\n').trim();

              const lines = doc.splitTextToSize(plain, usableW) as string[];
              const lineH = fs * 0.35;
              return padY * 4 + lines.length * lineH + 2;
            }

            const lines = doc.splitTextToSize(raw.trim(), usableW) as string[];
            const lineH = fs * 0.35;
            return padY * 4 + lines.length * lineH + 2;
          };

          for (let i = 0; i < roomsRowsCount; i++) {
            const row = twoColFullBody[i];

            const leftRaw = (row?.[commentLeftIdx] ?? '').toString();
            const rightRaw = (row?.[commentRightIdx] ?? '').toString();

            let needed = rowHeightsTwoCol[i] ?? 0;

            if (leftRaw.trim()) {
              needed = Math.max(needed, computeNeededFromRaw(leftRaw, colWLeft));
            }
            if (rightRaw.trim()) {
              needed = Math.max(needed, computeNeededFromRaw(rightRaw, colWRight));
            }

            rowHeightsTwoCol[i] = Math.max(needed, 15);
          }
        }

        const headerHeight2 = 9;
        const usableHeight2 = pageHeight - 25 - 12;
        const totalBodyHeight2 = rowHeightsTwoCol.reduce((a, b) => a + b, 0);
        const targetBodyHeight2 = usableHeight2 - headerHeight2;

        const footerContentUsableWidthTwoCol = pdfWidth - firstColLeft2 - 2 * 1.5;

        for (let i = roomsRowsCount; i < twoColFullBody.length; i++) {
          const row = twoColFullBody[i];
          const isFooterRow = row?.length >= 2 && typeof row[1] === 'object' && row[1] !== null && 'colSpan' in row[1];
          if (!isFooterRow) {
            continue;
          }

          const label = typeof row[0] === 'string' ? row[0] : '';
          const neededLabel = computeNeededHeightForFooterLabel(label, firstColLeft2);
          const cellObj = row[1];
          const text = typeof cellObj?.content === 'string' ? cellObj.content : '';

          if (text && this.looksLikeHtml(text)) {
            const trimTrailingBreaks = (s: string) => (s || '').replace(/(?:\s|<br\s*\/?>|<\/p>)+$/gi, '');

            const cleanedHtml = trimTrailingBreaks(text);
            const tokens = this.htmlToTokens(cleanedHtml);

            const textLines = tokens.filter((t) => t.type === 'newline').length + 1;

            const hasTags = this.extractTagsFromText(cleanedHtml).tags.length > 0;
            const tagLines = hasTags ? 1 : 0;

            const lineHeight = 7.2 * fontSizeRatio;
            const padY = 1.5;

            const totalLines = textLines + tagLines + 1;
            const neededHtml = padY * 2 + totalLines * lineHeight;

            rowHeightsTwoCol[i] = Math.max(rowHeightsTwoCol[i] ?? 0, neededLabel, neededHtml);
            continue;
          }

          const needed = computeNeededHeightForText(text, footerContentUsableWidthTwoCol, minFooterFontSize);
          rowHeightsTwoCol[i] = Math.max(rowHeightsTwoCol[i] ?? 0, neededLabel, needed);
        }

        //stretch all rows if total height is below available page space
        if (totalBodyHeight2 < targetBodyHeight2) {
          const extraSpace2 = targetBodyHeight2 - totalBodyHeight2;
          const roomsCount2 = roomsRowsCount;
          const delta2 = roomsCount2 > 0 ? extraSpace2 / roomsCount2 : 0;

          rowHeightsTwoCol = rowHeightsTwoCol.map((h, idx) => (idx < roomsRowsCount ? h + delta2 : h));
        }

        const footerBodyTwoCol = footerBody;

        const roomsHeightsTwoCol = rowHeightsTwoCol.slice(0, roomsRowsCount);
        const footerHeightsTwoCol = rowHeightsTwoCol.slice(roomsRowsCount);

        autoTable(doc as any, {
          head: headTwoCol,
          body: twoColBodyRooms,
          startY: 25,
          pageBreak: 'auto',
          showHead: 'everyPage',
          rowPageBreak: 'avoid',
          styles: {
            fontSize: 9 * fontSizeRatio,
            fontStyle: 'normal',
            textColor: [0, 0, 0],
            fillColor: [255, 255, 255],
            lineColor: [0, 0, 0],
            lineWidth: 0.05,
            halign: 'left',
            valign: 'middle',
            cellPadding: 1,
            overflow: 'linebreak',
            cellWidth: 'wrap',
          },
          bodyStyles: {
            fontStyle: 'normal',
            minCellHeight: 7,
            cellPadding: 1,
            halign: 'left',
            valign: 'middle',
          },
          headStyles: {
            fillColor: [200, 230, 255],
            fontStyle: 'bold',
            fontSize: 10 * fontSizeRatio,
            halign: 'center',
            valign: 'middle',
            minCellHeight: 9,
          },
          columnStyles: columnStylesTwoCol,
          margin: { top: 25, left: 2, right: 2, bottom: 4 },
          didDrawPage: didDrawPageCommon,
          didParseCell: createDidParseCellTwoCols(
            roomsHeightsTwoCol,
            roomSpansLeft,
            roomSpansRight,
            rightSalleColIndex,
            roomsRowsCount
          ),
          didDrawCell: didDrawCellTwoCols(
            cellColorsTwoCol,
            roomsRowsCount,
            doc,
            roomSpansLeft,
            roomSpansRight,
            rightSalleColIndex,
            fontSizeRatio
          ),
        });
        if (footerBodyTwoCol.length > 0) {
          let startYFooter = ((doc as any).lastAutoTable?.finalY ?? 25) + 1;
          if (startYFooter > pageHeight - 20) {
            doc.addPage();
            startYFooter = 25;
          }

          uniformFooterFontSizeTwoCol = this.computeUniformFooterFontSize(
            doc,
            footerBodyTwoCol,
            footerHeightsTwoCol,
            footerContentUsableWidthTwoCol,
            minFooterFontSize
          );

          uniformFooterFontSize = uniformFooterFontSizeTwoCol;
          autoTable(doc as any, {
            head: [],
            body: footerBodyTwoCol,
            startY: startYFooter,
            pageBreak: 'auto',
            showHead: 'never',
            rowPageBreak: 'avoid',
            styles: {
              fontSize: 9 * fontSizeRatio,
              fontStyle: 'normal',
              textColor: [0, 0, 0],
              fillColor: [255, 255, 255],
              lineColor: [0, 0, 0],
              lineWidth: 0.05,
              halign: 'left',
              valign: 'middle',
              cellPadding: 1,
              overflow: 'linebreak',
              cellWidth: 'wrap',
            },
            bodyStyles: {
              fontStyle: 'normal',
              minCellHeight: 7,
              cellPadding: 1,
              halign: 'left',
              valign: 'middle',
            },
            columnStyles: columnStylesTwoCol,
            margin: { top: 25, left: 2, right: 2, bottom: 4 },
            didDrawPage: didDrawPageCommon,
            didParseCell: createDidParseCell(footerHeightsTwoCol, {}, false, 0),
            didDrawCell: didDrawCellOneCol({}, 0, doc, {}, fontSizeRatio),
          });
        }
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 10);
      const fullFileName = `${fileName}_${timestamp}.pdf`;
      doc.save(fullFileName);
    } catch (error) {
      console.error('Error saving daily PDF file:', error);
      throw error;
    }
  }

  private constructDailyPDFTableData(
    dailyData: any[],
    showComments: boolean,
    intervenantColumns: {
      groupId: string;
      groupName: string;
      header: string;
      formationIds: string[];
    }[],
    roomsName: string
  ) {
    //build dynamic headers from intervenantcolumns
    const dynamicHeaders = intervenantColumns.map((c) => c.header);
    const roomsHeader = roomsName && roomsName.trim().length > 0 ? roomsName.trim() : 'Salle';

    const headRow = showComments
      ? [roomsHeader, 'Vacations', ...dynamicHeaders, 'Commentaire']
      : [roomsHeader, 'Vacations', ...dynamicHeaders];

    const headerData = {
      head: [headRow],
    };

    const tableData: any[] = [];
    const roomSpans: { [rowIndex: number]: { span: number; isStart: boolean } } = {};
    const cellColors: { [rowIndex: number]: { [colIndex: number]: string } } = {};
    const notRooms: string[] = [];

    let currentRowIndex = 0;

    const sectionsOrder = ['BLOC'];

    for (const section of sectionsOrder) {
      const rowsSection = dailyData.filter((r) => r.section === section);
      if (!rowsSection.length) {
        continue;
      }

      let i = 0;
      while (i < rowsSection.length) {
        const salle = rowsSection[i].salle;
        const groupSalle = rowsSection.filter((r) => r.salle === salle);

        if (section === 'BLOC') {
          const base = groupSalle[0];
          //normalize vacations into an array of lines
          const vacations: string[] =
            base.vacationLines && Array.isArray(base.vacationLines)
              ? base.vacationLines
              : String(base.vacation || '')
                  .split(/\r?\n/)
                  .map((s: string) => s.trim())
                  .filter(Boolean);

          const specialtyColors: (string | undefined)[] = base.specialtyColors || [];
          const perVacation = base.intervenantsPerGroupByVacation || [];

          const subCount = Math.max(1, vacations.length);

          for (let k = 0; k < subCount; k++) {
            const row: any[] = [];

            row.push(salle || '');
            row.push(vacations[k] || '');

            const groupsForThisVac = perVacation[k] || {};

            intervenantColumns.forEach((col) => {
              row.push(groupsForThisVac[col.groupId] || '');
            });

            if (showComments) {
              row.push(k === 0 ? base.commentPerRoom || '' : '');
            }

            tableData.push(row);

            const rowIndex = currentRowIndex;
            //vertical merge for salle column
            if (subCount > 1) {
              roomSpans[rowIndex] = { span: subCount, isStart: k === 0 };
            }

            const specialtyColorForRow = specialtyColors[k];
            if (specialtyColorForRow) {
              cellColors[rowIndex] = cellColors[rowIndex] || {};
              cellColors[rowIndex][1] = specialtyColorForRow;
            }

            currentRowIndex++;
          }

          i += groupSalle.length;
          continue;
        }
      }
    }

    //footer
    const footerStartIndex = tableData.length;

    //total number of columns in the table
    const totalCols = headRow.length;
    const footerColSpan = Math.max(1, totalCols - 1);

    //consultations
    const consultationItems = dailyData.filter((r) => r.section === 'CONSULTATION');
    if (consultationItems.length > 0) {
      const allAnesthetists: string[] = [];
      consultationItems.forEach((item) => {
        if (item.anesth) {
          allAnesthetists.push(item.anesth);
        }
      });

      if (allAnesthetists.length > 0) {
        //merge all anesthesiologists on one line and remove line breaks
        const consultationText = allAnesthetists.join(', ').replace(/\n/g, ' ').replace(/\r/g, ' ');
        tableData.push([
          'Consultation',
          {
            content: consultationText,
            colSpan: footerColSpan,
            styles: {
              halign: 'left',
              valign: 'middle',
            },
          },
        ]);
      }
    }

    //extra clinique
    const extraItems = dailyData.filter((r) => r.section === 'EXTRA_CLINIQUE');
    if (extraItems.length > 0) {
      const allAnesthetists: string[] = [];

      extraItems.forEach((item) => {
        if (!item.anesth) {
          return;
        }

        const lines = String(item.anesth)
          .split(/\r?\n/)
          .map((s: any) => s.trim())
          .filter(Boolean);

        allAnesthetists.push(...lines);
      });

      if (allAnesthetists.length > 0) {
        const extraText = allAnesthetists.join(', ');
        tableData.push([
          'Extra clinique',
          {
            content: extraText,
            colSpan: footerColSpan,
            styles: {
              halign: 'left',
              valign: 'middle',
            },
          },
        ]);
      }
    }

    //outofroom
    const roomName = roomsName && roomsName.trim().length > 0 ? roomsName.trim() : 'Salle';

    const horsSalleItems = dailyData.filter((r) => r.section === 'HORS_SALLE' && (r.comment ?? '').trim() !== '');

    horsSalleItems.forEach((item) => {
      const horsSalleText = (item.comment ?? '').trim();

      const label: string = item.label || `Hors ${roomName}`;

      tableData.push([
        label,
        {
          content: horsSalleText,
          colSpan: footerColSpan,
          styles: {
            halign: 'left',
            valign: 'middle',
          },
        },
      ]);
    });

    //comment global
    if (showComments) {
      const commentRow = dailyData.find((r) => r.section === 'COMMENT');
      const commentText = (commentRow?.comment ?? '').trim();

      notRooms.push('Commentaire');

      tableData.push([
        'Commentaire',
        {
          content: commentText,
          colSpan: footerColSpan,
          styles: { halign: 'left', valign: 'middle' },
        },
      ]);
    }

    return {
      tableData,
      headerData,
      roomSpans,
      vacationSpans: {},
      cellColors,
      footerStartIndex,
      headRow,
    };
  }

  private calculateDailyRowHeights(tableData: any[]): number[] {
    const baseHeight = 12;
    const lineHeight = 4;

    const baseHeightFooter = 8;
    const lineHeightFooter = 3;

    const baseHeightCommentFooter = 5;
    const lineHeightCommentFooter = 5;

    const getApproxCharsPerLine = (colIndex: number): number => {
      if (colIndex === 0) {
        return 10;
      }
      if (colIndex === 1) {
        return 25;
      }
      return 35;
    };

    const getCellText = (cell: any): string => {
      if (!cell) {
        return '';
      }
      if (typeof cell === 'string') {
        return cell;
      }
      if (typeof cell === 'object' && cell !== null) {
        if (typeof cell.content === 'string') {
          return cell.content;
        }
        if (Array.isArray(cell.content)) {
          return cell.content.join('\n');
        }
      }
      return '';
    };

    const countLinesForFooter = (text: string): number => {
      const t = (text ?? '').trim();
      if (!t) {
        return 1;
      }

      if (this.looksLikeHtml(t)) {
        const tokens = this.htmlToTokens(t);

        const newlineCount = tokens.filter((x) => x.type === 'newline').length;
        let lineCount = Math.max(1, newlineCount + 1);
        let tagItemsCount = 0;

        let currentText = '';
        for (const tk of tokens) {
          if (tk.type === 'newline') {
            const extracted = this.extractTagsFromText(currentText);
            if (extracted.tags && extracted.tags.length > 0) {
              tagItemsCount += 1;
            }
            currentText = '';
            continue;
          }

          currentText += tk.text;
        }

        //flush last
        const extractedLast = this.extractTagsFromText(currentText);
        if (extractedLast.tags && extractedLast.tags.length > 0) {
          tagItemsCount += 1;
        }

        lineCount = Math.max(1, lineCount - tagItemsCount);

        return lineCount;
      }

      const lines = t.split(/\r?\n/).filter((line) => line.trim().length > 0);
      return Math.max(1, lines.length);
    };

    return tableData.map((row: any[]) => {
      const isFooterRow = row.length >= 2 && typeof row[1] === 'object' && row[1] !== null && 'colSpan' in row[1];

      if (isFooterRow) {
        const isCommentFooter = typeof row[0] === 'string' && row[0].toString().toLowerCase().includes('commentaire');

        const cell = row[1];
        const text = getCellText(cell);

        const base = isCommentFooter ? baseHeightCommentFooter : baseHeightFooter;
        const lineH = isCommentFooter ? lineHeightCommentFooter : lineHeightFooter;

        const lineCount = countLinesForFooter(text);
        return base + (lineCount - 1) * lineH;
      }

      let maxLines = 1;

      row.forEach((cell: any, colIndex: number) => {
        const text = getCellText(cell).trim();
        if (!text) {
          return;
        }

        //HTML in body
        if (this.looksLikeHtml(text)) {
          const tokens = this.htmlToTokens(text);

          let approxLineCount = Math.max(1, tokens.filter((t) => t.type === 'newline').length + 1);

          let tagItemsCount = 0;
          let currentText = '';

          for (const tk of tokens) {
            if (tk.type === 'newline') {
              const extracted = this.extractTagsFromText(currentText);
              if (extracted.tags?.length) {
                tagItemsCount += 1;
              }
              currentText = '';
              continue;
            }
            currentText += tk.text;
          }

          const extractedLast = this.extractTagsFromText(currentText);
          if (extractedLast.tags?.length) {
            tagItemsCount += 1;
          }

          approxLineCount = Math.max(1, approxLineCount - tagItemsCount + 1);

          if (approxLineCount > maxLines) {
            maxLines = approxLineCount;
          }
          return;
        }

        const chunks = text
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter(Boolean);

        const approxCharsPerLine = getApproxCharsPerLine(colIndex);

        let totalLinesForCell = 0;
        chunks.forEach((chunk) => {
          const len = chunk.length;
          const estimatedLines = Math.max(1, Math.ceil(len / approxCharsPerLine));
          totalLinesForCell += estimatedLines;
        });

        if (totalLinesForCell > maxLines) {
          maxLines = totalLinesForCell;
        }
      });

      return baseHeight + (maxLines - 1) * lineHeight;
    });
  }

  private computeCellFontScaling(
    cellWidth: number,
    cellHeight: number,
    text: string,
    doc: jsPDF,
    minFontSize: number = 7
  ): { fontSize: number; lineHeight: number; lines: string[] } {
    const maxFontSize = 15;
    const padding = 2;

    const usableWidth = Math.max(1, cellWidth - padding * 2);
    const usableHeight = Math.max(1, cellHeight - padding * 2);

    let optimalFontSize = maxFontSize;
    let optimalLines: string[] = [];

    for (let fontSize = maxFontSize; fontSize >= minFontSize; fontSize -= 0.5) {
      doc.setFontSize(fontSize);

      const lines = doc.splitTextToSize(text, usableWidth) as string[];
      const lineHeight = fontSize * 0.35;
      const totalHeight = lines.length * lineHeight;

      if (totalHeight <= usableHeight) {
        optimalFontSize = fontSize;
        optimalLines = lines;
        break;
      }
    }

    if (optimalLines.length === 0) {
      optimalFontSize = minFontSize;
      doc.setFontSize(optimalFontSize);
      optimalLines = doc.splitTextToSize(text, usableWidth) as string[];
    }

    return {
      fontSize: optimalFontSize,
      lineHeight: optimalFontSize * 0.35,
      lines: optimalLines,
    };
  }

  private drawNameTimeWithScaling(
    doc: jsPDF,
    cell: any,
    rawText: string,
    halign: 'left' | 'center' | 'right',
    isTwoColumnMode: boolean = false,
    fixedFontSize?: number,
    minFontSizeOverride?: number
  ): void {
    if (!rawText || rawText.trim() === '') {
      return;
    }
    let fontSize: number;
    let lineHeight: number;
    let lines: string[];
    const footerLabels = ['Consultation', 'Extra clinique', 'Commentaire'];
    const isFooterLabel =
      footerLabels.some((label) => rawText && rawText.trim().toLowerCase() === label.toLowerCase()) ||
      rawText.trim().toLowerCase().startsWith('hors');
    const padding = 2;
    const usableWidth = Math.max(1, cell.width - padding * 2);

    if (isFooterLabel) {
      let fontSize: number;

      if (rawText.trim().toLowerCase() === 'commentaire') {
        fontSize = 9;
      } else {
        fontSize = 10;
      }

      const lines = doc.splitTextToSize(rawText, usableWidth) as string[];
      const lineHeight = fontSize * 0.35;

      doc.setFontSize(fontSize);
      doc.setFont('helvetica', 'bold');
      const totalTextHeight = lines.length * lineHeight;

      const startY = cell.y + (cell.height - totalTextHeight) / 2 + lineHeight * 0.7;

      lines.forEach((line, index) => {
        const lineWidth = doc.getTextWidth(line);
        const x = cell.x + (cell.width - lineWidth) / 2;
        const y = startY + index * lineHeight;

        doc.text(line, x, y);
      });

      return;
    }

    const colorTagRegex = /\[\[(#[0-9A-Fa-f]{6})\]\]/;

    const originalLines = rawText.split('\n').map((line) => {
      const m = line.match(colorTagRegex);
      const color = m ? m[1] : undefined;
      const clean = line.replace(/\[\[(#[0-9A-Fa-f]{6})\]\]/g, '').trimEnd();

      return { clean, color };
    });

    const displayText = originalLines.map((l) => l.clean).join('\n');

    const minFs = minFontSizeOverride ?? 7;

    if (isTwoColumnMode) {
      fontSize = 10;
      lineHeight = fontSize * 0.35;
    } else if (fixedFontSize) {
      fontSize = fixedFontSize;
      lineHeight = fontSize * 0.35;
    } else {
      const result = this.computeCellFontScaling(cell.width, cell.height, displayText, doc, minFs);
      fontSize = result.fontSize;
      lineHeight = result.lineHeight;
      lines = result.lines;
    }

    doc.setFontSize(fontSize);
    lines = doc.splitTextToSize(displayText, usableWidth) as string[];

    if (!isTwoColumnMode && !fixedFontSize) {
      const totalHeight = lines.length * lineHeight;
      const usableHeight = Math.max(1, cell.height - padding * 2);

      if (totalHeight > usableHeight) {
        const result = this.computeCellFontScaling(cell.width, cell.height, displayText, doc);
        fontSize = result.fontSize;
        lineHeight = result.lineHeight;
        lines = result.lines;
      }
    }

    doc.setFontSize(fontSize);

    const centerY = cell.y + cell.height / 2;
    const blockHeight = (lines.length - 1) * lineHeight;
    const firstLineY = centerY - blockHeight / 2 + lineHeight / 2;

    const timePattern = /\(\s*\d{2}h\d{2}\s*[-–]\s*\d{2}h\d{2}\s*\)/g;

    lines.forEach((line, idx) => {
      const rawLineLocal = (line || '').replace(/\s+$/g, '');
      if (!rawLineLocal) {
        return;
      }

      const y = firstLineY + idx * lineHeight;

      const trimmed = rawLineLocal.trim();
      let lineColor: string | undefined = undefined;

      if (trimmed) {
        for (const orig of originalLines) {
          if (orig.clean.includes(trimmed)) {
            lineColor = orig.color;
            break;
          }
        }
      }

      timePattern.lastIndex = 0;
      const hasTime = timePattern.test(rawLineLocal);
      timePattern.lastIndex = 0;

      let timeRectX = 0;
      let timeRectWidth = 0;
      let timeTextContent = '';

      if (hasTime && lineColor) {
        const timeMatch = rawLineLocal.match(timePattern);
        if (timeMatch && timeMatch[0]) {
          timeTextContent = timeMatch[0];
          const textDims = doc.getTextDimensions(timeTextContent);
          timeRectWidth = textDims.w;
          const rectHeight = textDims.h;

          let xStart: number;
          const totalWidth = Math.min(doc.getTextWidth(rawLineLocal), usableWidth);

          if (halign === 'center') {
            xStart = cell.x + (cell.width - totalWidth) / 2;
          } else if (halign === 'right') {
            xStart = cell.x + cell.width - padding - totalWidth;
          } else {
            xStart = cell.x + padding;
          }

          const timeIndex = rawLineLocal.indexOf(timeTextContent);
          timeRectX = xStart;

          if (timeIndex > 0) {
            const beforeTime = rawLineLocal.substring(0, timeIndex);
            doc.setFont('helvetica', 'bold');
            const beforeWidth = doc.getTextWidth(beforeTime);
            timeRectX = xStart + beforeWidth;

            if (beforeTime.endsWith(' ')) {
              timeRectX += doc.getTextWidth(' ');
            }
          }

          doc.setFillColor(lineColor);
          doc.setDrawColor(lineColor);
          const padY = rectHeight * 0.2;

          doc.roundedRect(timeRectX, y - rectHeight, timeRectWidth, rectHeight + padY * 2, 1.5, 1.5, 'F');
        }
      }

      if (!hasTime) {
        const doctorRegex = /((?:Dr\.|[A-Z]\.)\s+[\p{L}\p{M}\s.'-]+)/gu;
        doctorRegex.lastIndex = 0;
        const hasDoctor = doctorRegex.test(rawLineLocal);
        doctorRegex.lastIndex = 0;

        doc.setFont('helvetica', 'normal');
        const totalWidthNoTime = Math.min(doc.getTextWidth(rawLineLocal), usableWidth);
        let xStart: number;
        if (halign === 'center') {
          xStart = cell.x + (cell.width - totalWidthNoTime) / 2;
        } else if (halign === 'right') {
          xStart = cell.x + cell.width - padding - totalWidthNoTime;
        } else {
          xStart = cell.x + padding;
        }
        let xCursor = xStart;

        if (!hasDoctor) {
          doc.setFont('helvetica', 'bold');
          doc.text(rawLineLocal, xStart, y);
          return;
        }

        let lastIndex = 0;
        let m: RegExpExecArray | null;

        while ((m = doctorRegex.exec(rawLineLocal)) !== null) {
          const before = rawLineLocal.slice(lastIndex, m.index);
          const doctorName = m[1];

          if (before) {
            doc.setFont('helvetica', 'normal');
            doc.text(before, xCursor, y);
            xCursor += doc.getTextWidth(before);
          }

          if (doctorName) {
            doc.setFont('helvetica', 'bold');
            doc.text(doctorName, xCursor, y);
            xCursor += doc.getTextWidth(doctorName);
          }

          lastIndex = doctorRegex.lastIndex;
        }

        const after = rawLineLocal.slice(lastIndex);
        if (after) {
          doc.setFont('helvetica', 'normal');
          doc.text(after, xCursor, y);
        }

        return;
      }

      const logicalLine = rawLineLocal.replace(/\s+/g, ' ');
      doc.setFont('helvetica', 'normal');
      const totalWidth = Math.min(doc.getTextWidth(logicalLine), usableWidth);

      let xStart: number;
      if (halign === 'center') {
        xStart = cell.x + (cell.width - totalWidth) / 2;
      } else if (halign === 'right') {
        xStart = cell.x + cell.width - padding - totalWidth;
      } else {
        xStart = cell.x + padding;
      }

      let xCursor = xStart;
      let lastIndex = 0;
      let m: RegExpExecArray | null;

      const oneSpace = ' ';
      const wSpace = doc.getTextWidth(oneSpace);

      timePattern.lastIndex = 0;
      while ((m = timePattern.exec(rawLineLocal)) !== null) {
        const timeRaw = m[0];
        const beforeRaw = rawLineLocal.slice(lastIndex, m.index);

        const before = beforeRaw.replace(/\s+$/g, '');
        const timeText = timeRaw.replace(/\s+/g, ' ');

        if (before) {
          doc.setFont('helvetica', 'bold');
          const wBefore = doc.getTextWidth(before);
          doc.text(before, xCursor, y);
          xCursor += wBefore;
        }

        if (before && timeText) {
          doc.setFont('helvetica', 'normal');
          doc.text(oneSpace, xCursor, y);
          xCursor += wSpace;
        }

        if (timeText) {
          doc.setFont('helvetica', 'normal');

          if (lineColor) {
            const adaptiveTextColor = this.getTextColorForBackground(lineColor);
            if (adaptiveTextColor === 'FFFFFFFF') {
              doc.setTextColor(255, 255, 255);
            } else {
              doc.setTextColor(0, 0, 0);
            }
          }

          const wTime = doc.getTextWidth(timeText);

          if (timeRectX > 0) {
            doc.text(timeText, timeRectX, y);
            xCursor = timeRectX + wTime;
          } else {
            doc.text(timeText, xCursor, y);
            xCursor += wTime;
          }

          doc.setTextColor(0, 0, 0);
        }

        lastIndex = m.index + timeRaw.length;
      }
      timePattern.lastIndex = 0;

      const remainingRaw = rawLineLocal.slice(lastIndex);
      const remaining = remainingRaw.replace(/^\s+/, '');

      if (remaining) {
        doc.setFont('helvetica', 'bold');

        const firstChar = remaining.charAt(0);
        const startsWithPunctuation = /^[,]/.test(firstChar);

        if (xCursor > xStart && !startsWithPunctuation) {
          doc.text(oneSpace, xCursor, y);
          xCursor += wSpace;
        }

        doc.text(remaining, xCursor, y);
      }
    });
  }

  private computeUniformFooterFontSize(
    doc: jsPDF,
    footerBody: any[][],
    footerHeights: number[],
    footerContentUsableWidth: number,
    minFooterFontSize: number
  ): number {
    const padding = 2;

    let minFound = Number.POSITIVE_INFINITY;

    for (let i = 0; i < footerBody.length; i++) {
      const row = footerBody[i];
      const cellObj = row?.[1];

      if (!cellObj || typeof cellObj !== 'object') {
        continue;
      }

      const text = typeof cellObj.content === 'string' ? cellObj.content.trim() : '';
      if (!text) {
        continue;
      }
      if (this.looksLikeHtml(text)) {
        continue;
      }

      const h = footerHeights[i] ?? 7;

      const simulatedCellWidth = footerContentUsableWidth + padding * 2;
      const simulatedCellHeight = h;

      const r = this.computeCellFontScaling(simulatedCellWidth, simulatedCellHeight, text, doc, minFooterFontSize);

      if (r.fontSize < minFound) {
        minFound = r.fontSize;
      }
    }

    if (!Number.isFinite(minFound)) {
      return minFooterFontSize;
    }

    return Math.max(minFooterFontSize, minFound);
  }

  private looksLikeHtml(s: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(s);
  }

  private decodeHtmlEntities(s: string): string {
    return (s || '').replace(/&nbsp;/g, ' ');
  }

  private parseCssColor(value: string | null | undefined): [number, number, number] | undefined {
    if (!value) {
      return undefined;
    }

    const cleaned = value.trim().toLowerCase().replace(/\s+/g, '');

    const m = cleaned.match(/^rgb\((\d{1,3}),(\d{1,3}),(\d{1,3})\)$/i);
    if (!m) {
      return undefined;
    }
    const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
    const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
    const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
    return [r, g, b];
  }

  private readInlineStyle(el: HTMLElement): Partial<TextStyle> {
    const st = (el.getAttribute('style') || '').trim();

    const out: Partial<TextStyle> = {};

    const colorMatch = st.match(/(?:^|;)\s*color\s*:\s*([^;]+)\s*(?:;|$)/i);
    if (colorMatch) {
      out.textColor = this.parseCssColor(colorMatch[1]?.trim());
    }

    const bgMatch = st.match(/(?:^|;)\s*background-color\s*:\s*([^;]+)\s*(?:;|$)/i);
    if (bgMatch) {
      out.bgColor = this.parseCssColor(bgMatch[1]?.trim());
    }

    return out;
  }

  private mergeStyle(base: TextStyle, extra: Partial<TextStyle>): TextStyle {
    return {
      bold: extra.bold ?? base.bold,
      underline: extra.underline ?? base.underline,
      textColor: extra.textColor ?? base.textColor,
      bgColor: extra.bgColor ?? base.bgColor,
    };
  }

  private htmlToTokens(html: string): HtmlToken[] {
    const tokens: HtmlToken[] = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || '', 'text/html');

    const pushText = (t: string, style: TextStyle) => {
      const txt = this.decodeHtmlEntities(t).replace(/\s+/g, ' ');
      if (!txt.trim()) {
        return;
      }
      tokens.push({ type: 'text', text: txt, style });
    };

    const pushNewline = () => {
      if (tokens.length && tokens[tokens.length - 1].type !== 'newline') {
        tokens.push({ type: 'newline' });
      }
    };

    const olStack: number[] = [];

    const walk = (node: Node, style: TextStyle) => {
      if (node.nodeType === Node.TEXT_NODE) {
        pushText((node as Text).data, style);
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      if (tag === 'br') {
        pushNewline();
        return;
      }

      if (tag === 'p' || tag === 'div') {
        pushNewline();
        Array.from(el.childNodes).forEach((c) => walk(c, style));
        pushNewline();
        return;
      }

      if (tag === 'strong' || tag === 'b') {
        const inline = this.readInlineStyle(el);
        const s = this.mergeStyle(style, { bold: true, ...inline });
        Array.from(el.childNodes).forEach((c) => walk(c, s));
        return;
      }

      if (tag === 'u') {
        const inline = this.readInlineStyle(el);
        const s = this.mergeStyle(style, { underline: true, ...inline });
        Array.from(el.childNodes).forEach((c) => walk(c, s));
        return;
      }

      if (tag === 'span') {
        const inline = this.readInlineStyle(el);
        const s = this.mergeStyle(style, inline);
        Array.from(el.childNodes).forEach((c) => walk(c, s));
        return;
      }

      if (tag === 'ol') {
        olStack.push(0);
        pushNewline();
        Array.from(el.childNodes).forEach((c) => walk(c, style));
        pushNewline();
        olStack.pop();
        return;
      }

      if (tag === 'ul') {
        pushNewline();
        Array.from(el.childNodes).forEach((c) => walk(c, style));
        pushNewline();
        return;
      }

      if (tag === 'li') {
        pushNewline();

        const isOl = olStack.length > 0;
        if (isOl) {
          olStack[olStack.length - 1] += 1;
          pushText(`${olStack[olStack.length - 1]}. `, style);
        } else {
          pushText(`• `, style);
        }

        Array.from(el.childNodes).forEach((c) => walk(c, style));
        pushNewline();
        return;
      }

      Array.from(el.childNodes).forEach((c) => walk(c, style));
    };

    walk(doc.body, {});

    while (tokens.length && tokens[0].type === 'newline') {
      tokens.shift();
    }
    while (tokens.length && tokens[tokens.length - 1].type === 'newline') {
      tokens.pop();
    }

    return tokens;
  }

  private drawHtmlInCell(doc: jsPDF, cell: any, html: string, fontSizeRatio: number): void {
    if (!html) {
      return;
    }

    const tokens = this.htmlToTokens(html);
    if (!tokens.length) {
      return;
    }

    const padX = 1.5;
    const padY = 1.5;

    const x0 = cell.x + padX;
    const xMax = cell.x + cell.width - padX;

    const fontSize = 13 * fontSizeRatio;
    const lineHeight = fontSize * 0.35;

    const yLimit = () => cell.y + cell.height - padY;

    const splitKeepSpaces = (s: string) => s.split(/(\s+)/).filter((p) => p.length > 0);

    const applyStyle = (s: TextStyle) => {
      doc.setFont('helvetica', s.bold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);

      const c = getEffectiveTextColor(s);
      doc.setTextColor(c[0], c[1], c[2]);
    };

    const getContrastTextColor = (bg: [number, number, number]): [number, number, number] => {
      const [r, g, b] = bg;
      const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return lum < 140 ? [255, 255, 255] : [0, 0, 0];
    };

    const getEffectiveTextColor = (s: TextStyle): [number, number, number] => {
      if (s.textColor) {
        return s.textColor;
      }
      if (s.bgColor) {
        return getContrastTextColor(s.bgColor);
      }
      return [0, 0, 0];
    };

    const drawTagsAfterText = (yBaseline: number, xEnd: number, tags: string[]): number => {
      if (!tags?.length) {
        return 0;
      }

      const tagFontSizeLocal = 7 * fontSizeRatio;
      const tagPaddingX = 1;
      const tagPaddingY = 0.4;
      const radius = 1.5;
      const gapAfterText = 1.4;
      const gapBetweenTags = 1.3;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(tagFontSizeLocal);

      const measureTagW = (label: string) => doc.getTextWidth(label) + tagPaddingX * 2;

      const tagHeight = 3 * fontSizeRatio + tagPaddingY * 2;
      const rectYFor = (yB: number) => yB - tagHeight / 2 - 1;

      let lineUsed = 0;
      let x = xEnd + gapAfterText;

      const totalTagsWidth = tags.reduce((acc, t, idx) => acc + measureTagW(t) + (idx ? gapBetweenTags : 0), 0);

      if (x + totalTagsWidth > xMax) {
        lineUsed = 1;
        x = x0;
        yBaseline += lineHeight;
      }

      for (const rawLabel of tags) {
        const label = rawLabel;
        const tagW = measureTagW(label);

        if (x + tagW > xMax && x > x0) {
          lineUsed += 1;
          x = x0;
          yBaseline += lineHeight;
        }

        const rectY = rectYFor(yBaseline);

        doc.setFillColor(210, 235, 255);
        doc.setDrawColor(210, 235, 255);
        doc.roundedRect(x, rectY, tagW, tagHeight, radius, radius, 'F');

        doc.setTextColor(0, 136, 204);
        doc.text(label, x + tagW / 2, yBaseline, { align: 'center' });

        doc.setTextColor(0, 0, 0);

        x += tagW + gapBetweenTags;
      }

      return lineUsed;
    };

    type Item = { parts: { text: string; style: TextStyle }[]; tags: string[]; blank?: boolean };
    const items: Item[] = [];

    let cur: Item = { parts: [], tags: [] };
    let lastWasNewline = false;

    const pushCur = () => {
      const hasText = cur.parts.some((p) => (p.text || '').trim().length > 0);
      const hasTags = cur.tags.length > 0;
      if (hasText || hasTags) {
        items.push(cur);
      }
      cur = { parts: [], tags: [] };
    };

    for (const t of tokens) {
      if (t.type === 'newline') {
        if (!lastWasNewline) {
          pushCur();
        } else {
          items.push({ parts: [], tags: [], blank: true });
        }
        lastWasNewline = true;
        continue;
      }

      lastWasNewline = false;

      const extracted = this.extractTagsFromText(t.text);

      extracted.tags.forEach((tg) => {
        if (tg && !cur.tags.includes(tg)) {
          cur.tags.push(tg);
        }
      });

      if (extracted.clean) {
        cur.parts.push({ text: extracted.clean, style: t.style });
      }
    }
    pushCur();

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.blank) {
        continue;
      }

      const hasText = (it.parts || []).some((p) => (p.text || '').trim().length > 0);
      const hasTags = (it.tags || []).length > 0;

      if (!hasText && hasTags) {
        for (let j = i - 1; j >= 0; j--) {
          const prev = items[j];
          if (prev.blank) {
            continue;
          }

          prev.tags = prev.tags || [];
          it.tags.forEach((t) => {
            if (t && !prev.tags.includes(t)) {
              prev.tags.push(t);
            }
          });

          items.splice(i, 1);
          i -= 1;
          break;
        }
      }
    }
    while (items.length && items[items.length - 1]?.blank) {
      items.pop();
    }

    let y = cell.y + padY + fontSize * 0.85;

    for (const it of items) {
      if (y > yLimit()) {
        return;
      }

      if (it.blank) {
        y += lineHeight * 0.8;
        continue;
      }

      let x = x0;
      let lastLineXEnd = x0;
      let lastLineY = y;

      const newLine = () => {
        x = x0;
        y += lineHeight;
        lastLineY = y;
        lastLineXEnd = x0;
      };

      for (const p of it.parts) {
        const pieces = splitKeepSpaces(p.text);
        for (const piece of pieces) {
          if (y > yLimit()) {
            break;
          }
          if (!piece) {
            continue;
          }

          applyStyle(p.style);

          const w = doc.getTextWidth(piece);

          if (x + w > xMax && x > x0) {
            newLine();
          }
          if (y > yLimit()) {
            break;
          }

          // highlight
          if (p.style.bgColor) {
            const dims = doc.getTextDimensions(piece);
            const rectH = Math.max(2, dims.h * 1.15);
            const rectY = y - dims.h;

            doc.setFillColor(p.style.bgColor[0], p.style.bgColor[1], p.style.bgColor[2]);
            doc.rect(x, rectY, w, rectH, 'F');

            const c = getEffectiveTextColor(p.style);
            doc.setTextColor(c[0], c[1], c[2]);
          }

          doc.text(piece, x, y);

          // underline
          if (p.style.underline) {
            const c = getEffectiveTextColor(p.style);
            doc.setDrawColor(c[0], c[1], c[2]);

            doc.setLineWidth(0.2);
            doc.line(x, y + 0.7, x + w, y + 0.7);

            doc.setLineWidth(0.05);
            doc.setDrawColor(0, 0, 0);
          }

          x += w;
          lastLineXEnd = x;
        }
      }

      const endMinusSpace = Math.max(x0, lastLineXEnd - doc.getTextWidth(' ') * 0.8);
      const extraLines = drawTagsAfterText(lastLineY, endMinusSpace, it.tags);

      y += lineHeight * (1 + extraLines);
    }

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  }

  private isKnownTagLabel(s: string): string | null {
    const m = (s || '').trim().match(/^\(?\s*(IADES|Anesth|Infirmières|MERM|Radiologues)\s*\)?$/i);
    if (!m) {
      return null;
    }

    const displayMap: Record<string, string> = {
      Infirmières: 'Infirm',
      IADES: 'IADES',
      Anesth: 'Anesth',
      Radiologues: 'Radio',
      MERM: 'MERM',
    };
    const raw = m[1];
    return displayMap[raw] || displayMap[raw.toUpperCase()] || raw;
  }

  private extractTagsFromText(text: string): { clean: string; tags: string[] } {
    if (!text) {
      return { clean: '', tags: [] };
    }

    const tagWord = '(IADES|Anesth|Infirmières|MERM|Radiologues)';

    const tagRegex = new RegExp(String.raw`(?:\s*[\/|,-]\s*)?\(?\s*${tagWord}\s*\)?(?:\s*[\/|,-]\s*)?`, 'gi');

    const found: string[] = [];

    const clean = text.replace(tagRegex, (full) => {
      const m = full.match(new RegExp(tagWord, 'i'));
      const raw = m ? m[1] : '';
      const mapped = this.isKnownTagLabel(raw) || raw;

      if (mapped) {
        found.push(mapped);
      }
      return ' ';
    });

    const uniq: string[] = [];
    found.forEach((t) => {
      if (t && !uniq.includes(t)) {
        uniq.push(t);
      }
    });

    return { clean, tags: uniq };
  }

  async saveUnmetNeedsExcel(
    data: {
      nightData?: any[];
      dayBlocData?: any[];
      nightAnesthData?: any[];
      dayBlocAnesthData?: any[];
      dayConsultData?: any[];
      nightIadeData?: any[];
      dayBlocIadeData?: any[];
    },
    fileName: string
  ): Promise<void> {
    try {
      const workbook: Excel.Workbook = new Excel.Workbook();

      if (data.nightData?.length || data.nightIadeData?.length || data.nightAnesthData?.length) {
        const nightSheet = workbook.addWorksheet('Nuit', {
          views: [{ state: 'frozen', ySplit: 1, activeCell: 'A1' }],
        });
        const nightItems = [...(data.nightData || []), ...(data.nightIadeData || []), ...(data.nightAnesthData || [])];
        const nightRows = this.constructUnmetNeedsSheet(nightItems, 'night');
        this.populateUnmetNeedsSheet(nightSheet, nightRows, nightItems, 'night');
      }

      if (data.dayBlocData?.length || data.dayBlocIadeData?.length || data.dayBlocAnesthData?.length) {
        const dayBlocSheet = workbook.addWorksheet('Jour', {
          views: [{ state: 'frozen', ySplit: 1, activeCell: 'A1' }],
        });
        const dayBlocItems = [
          ...(data.dayBlocData || []),
          ...(data.dayBlocIadeData || []),
          ...(data.dayBlocAnesthData || []),
        ];
        const dayBlocRows = this.constructUnmetNeedsSheet(dayBlocItems, 'dayBloc');
        this.populateUnmetNeedsSheet(dayBlocSheet, dayBlocRows, dayBlocItems, 'dayBloc');
      }

      if (data.dayConsultData?.length) {
        const consultSheet = workbook.addWorksheet('Consultations', {
          views: [{ state: 'frozen', ySplit: 1, activeCell: 'A1' }],
        });
        const consultRows = this.constructConsultSheet(data.dayConsultData);
        this.populateUnmetNeedsSheet(consultSheet, consultRows, data.dayConsultData, 'consult');
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 10);
      const fullFileName = `${fileName}_${timestamp}.xlsx`;

      workbook.xlsx.writeBuffer().then((buffer: Excel.Buffer) => {
        this.save(buffer, fullFileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      });
    } catch (error) {
      console.error('Error saving unmet needs Excel:', error);
      throw error;
    }
  }

  private constructUnmetNeedsSheet(items: any[], type: 'night' | 'dayBloc'): any[][] {
    if (!items?.length) {
      return [];
    }

    const dates = [...new Set(items.map((i) => i.date))].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    const labelKey = type === 'night' ? 'code' : 'room';

    const labelMap = new Map<string, { label: string; startTime: string; endTime: string; color?: string }>();
    items.forEach((i) => {
      const label = i[labelKey];
      if (label && !labelMap.has(label)) {
        labelMap.set(label, {
          label,
          startTime: i.startTime ?? '',
          endTime: i.endTime ?? '',
          color: i.color ?? (Array.isArray(i.colors) ? i.colors[0] : undefined),
        });
      }
    });

    const labelMeta = [...labelMap.values()];

    const headerRow = [
      'Date',
      ...labelMeta.map((m) => (m.startTime && m.endTime ? `${m.label}\n${m.startTime}-${m.endTime}` : m.label)),
    ];

    const dataRows = dates.map((date) => {
      const row: any[] = [this.formatDate(date)];
      labelMeta.forEach(({ label }) => {
        const entry = items.find(
          (i) => i[labelKey] === label && new Date(i.date).getTime() === new Date(date).getTime()
        );
        if (!entry) {
          row.push('');
        } else {
          const unmet = entry.unmetNeed ?? entry.unmetNeeds ?? 0;
          row.push(`${unmet}`);
        }
      });
      return row;
    });

    return [headerRow, ...dataRows];
  }

  private constructConsultSheet(consultData: any[]): any[][] {
    if (!consultData?.length) {
      return [];
    }

    const dates = [...new Set(consultData.map((i) => i.date))].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    const specialties = [...new Set(consultData.map((i) => i.specialty).filter(Boolean))];

    const headerRow = ['Date', ...specialties];

    const dataRows = dates.map((date) => {
      const row: any[] = [this.formatDate(date)];
      specialties.forEach((specialty) => {
        const entry = consultData.find(
          (i) => i.specialty === specialty && new Date(i.date).getTime() === new Date(date).getTime()
        );
        if (!entry) {
          row.push('');
        } else {
          const parts: string[] = [];
          if (entry.unmetNeedsSenior) {
            parts.push(`Senior:${entry.unmetNeedsSenior}`);
          }
          if (entry.unmetNeedsJunior) {
            parts.push(`Junior:${entry.unmetNeedsJunior}`);
          }
          if (entry.unmetNeedsAnesth) {
            parts.push(`Anesth:${entry.unmetNeedsAnesth}`);
          }
          row.push(parts.length ? parts.join(' / ') : '✓');
        }
      });
      return row;
    });

    return [headerRow, ...dataRows];
  }

  private populateUnmetNeedsSheet(
    worksheet: Excel.Worksheet,
    rows: any[][],
    items: any[],
    type: 'night' | 'dayBloc' | 'consult'
  ): void {
    if (!rows.length) {
      return;
    }

    const labelKey = type === 'night' ? 'code' : type === 'dayBloc' ? 'room' : 'specialty';

    const labelColorMap = new Map<string, string>();
    items.forEach((i) => {
      const label = i[labelKey];
      if (label && !labelColorMap.has(label)) {
        const rawColor: string | undefined =
          type === 'night' ? i.color : Array.isArray(i.colors) ? i.colors[0] : undefined;
        if (rawColor) {
          labelColorMap.set(label, 'FF' + rawColor.replace('#', ''));
        }
      }
    });

    rows.forEach((row, rowIndex) => {
      const excelRow = worksheet.addRow(row);

      if (rowIndex === 0) {
        excelRow.eachCell((cell, colNumber) => {
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' },
          };

          if (colNumber === 1) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5496' } };
          } else {
            const cellText = String(cell.value ?? '');
            const pureLabel = cellText.split('\n')[0];
            const colorArgb = labelColorMap.get(pureLabel) ?? 'FF2F5496';
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colorArgb } };
          }
        });
      } else {
        excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (colNumber === 1) {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          } else {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          }

          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' },
          };
        });
      }
    });

    worksheet.columns.forEach((col, i) => {
      col.width = i === 0 ? 14 : 22;
    });
    worksheet.getRow(1).height = 35;
  }

  async exportUnmetNeedsExcel(dataArrays: any, fileName: string): Promise<void> {
    try {
      const workbook: Excel.Workbook = new Excel.Workbook();

      const sheetMap = new Map<string, any[]>();

      for (const groupObj of dataArrays) {
        if (
          (groupObj.unmetBlocNeeds && groupObj.unmetBlocNeeds.length > 0) ||
          (groupObj.unmetConsultNeeds && groupObj.unmetConsultNeeds.length > 0)
        ) {
          const fg = groupObj.formationGroup;
          const displayName = fg.displayName ?? fg.name ?? 'Sheet';
          const isNurses =
            fg.smartplanningMode?.toUpperCase() === 'NURSE' || fg.smartplanningMode?.toUpperCase() === 'IADE';

          const blocRows = this.buildBlocRows(groupObj.unmetBlocNeeds ?? [], isNurses);
          const consultRows = this.buildConsultRows(groupObj.unmetConsultNeeds ?? []);

          const existing = sheetMap.get(displayName) ?? [];
          sheetMap.set(displayName, [...existing, ...blocRows, ...consultRows]);
        }
      }

      for (const [sheetName, rows] of sheetMap.entries()) {
        const ws = workbook.addWorksheet(sheetName.slice(0, 31), {
          views: [{ state: 'frozen', ySplit: 1, activeCell: 'A1' }],
        });
        this.populateNeedsSheet(ws, rows);
      }
      if (sheetMap.size === 0) {
        const ws = workbook.addWorksheet('Aucun besoin manquant');
        ws.addRow(['Aucun besoin manquant sur la période sélectionnée']);
        ws.getRow(1).getCell(1).font = { italic: true, color: { argb: 'FF888888' }, name: 'Arial', size: 10 };
        ws.getColumn(1).width = 50;
      }

      const fullFileName = `${fileName}.xlsx`;

      workbook.xlsx.writeBuffer().then((buffer: Excel.Buffer) => {
        this.save(buffer, fullFileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      });
    } catch (error) {
      console.error('Error exporting unmet needs Excel :', error);
      throw error;
    }
  }

  private buildBlocRows(items: any[], isNurses: boolean): any[] {
    const rows: any[] = [];

    for (const item of items) {
      const date = this.formatDateFrench(item.date);
      const schedule = this.formatTimeRange(item.startTime, item.endTime);
      const room = item.room ?? '';
      const specialty = Array.isArray(item.specialties) ? item.specialties.join(', ') : (item.specialties ?? '');

      if (isNurses) {
        const unmet = item.unmetNeeds ?? 0;
        for (let i = 0; i < Math.max(unmet, 1); i++) {
          rows.push({
            date,
            schedule,
            room,
            specialty,
            seniority: '',
            role: item.role ?? '',
            unmet: unmet > 0 ? 1 : 0,
          });
        }
      } else {
        const seniorityEntries: Array<[string, number]> = [
          ['Senior', item.unmetNeedsSenior ?? 0],
          ['Junior', item.unmetNeedsJunior ?? 0],
          ['Interne', item.unmetNeedsIntern ?? 0],
          ['Anesthésiste', item.unmetNeedsAnesth ?? 0],
        ];
        for (const [seniority, unmet] of seniorityEntries) {
          if (unmet > 0) {
            for (let i = 0; i < unmet; i++) {
              rows.push({ date, schedule, room, specialty, seniority, role: '', unmet: 1 });
            }
          }
        }
      }
    }

    return rows;
  }

  private buildConsultRows(items: any[]): any[] {
    const rows: any[] = [];

    for (const item of items) {
      const date = this.formatDateFrench(item.date);
      const specialty = item.specialty ?? '';

      const seniorityEntries: Array<[string, number]> = [
        ['Senior', item.unmetNeedsSenior ?? 0],
        ['Junior', item.unmetNeedsJunior ?? 0],
        ['Anesthésiste', item.unmetNeedsAnesth ?? 0],
      ];
      for (const [seniority, unmet] of seniorityEntries) {
        if (unmet > 0) {
          for (let i = 0; i < unmet; i++) {
            rows.push({
              date,
              schedule: '',
              room: 'Consultation (hors Salle)',
              specialty,
              seniority,
              role: '',
              unmet: 1,
            });
          }
        }
      }
    }

    return rows;
  }

  private populateNeedsSheet(worksheet: Excel.Worksheet, rows: any[]): void {
    const headers = ['Date', 'Horaire de la vacation', 'Salle', 'Spécialité', 'Séniorité', 'Role', 'Nom', 'Prénom'];

    const headerRow = worksheet.addRow(headers);
    headerRow.height = 20;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Arial', size: 10 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5496' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = this.thinBorder();
    });

    for (const row of rows) {
      const excelRow = worksheet.addRow([
        row.date,
        row.schedule,
        row.room,
        row.specialty,
        row.seniority,
        row.role,
        '',
        '',
      ]);

      excelRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (colNumber === 1) {
          cell.font = { bold: true, name: 'Arial', size: 10 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
          cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        } else {
          cell.font = { name: 'Arial', size: 10 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        }
        cell.border = this.thinBorder();
      });
    }

    const colWidths = [28, 25, 28, 28, 14, 20, 18, 18];
    colWidths.forEach((width, i) => {
      worksheet.getColumn(i + 1).width = width;
    });
    const columnsToRemove: number[] = [];

    const protectedColumns = new Set([headers.indexOf('Nom') + 1, headers.indexOf('Prénom') + 1]);

    worksheet.columns.forEach((col, colIndex) => {
      if (!col) {
        return;
      }

      const colNumber = colIndex + 1;
      if (protectedColumns.has(colNumber)) {
        return;
      }

      let isEmpty = true;
      if (col.eachCell) {
        col.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
          if (rowNumber === 1) {
            return;
          }
          if (cell.value !== null && cell.value !== undefined && cell.value !== '') {
            isEmpty = false;
          }
        });
      }

      if (isEmpty) {
        columnsToRemove.push(colNumber);
      }
    });
    columnsToRemove
      .sort((a, b) => b - a)
      .forEach((colIndex) => {
        worksheet.spliceColumns(colIndex, 1);
      });
  }

  private thinBorder(): Partial<Excel.Borders> {
    const thin: Excel.BorderStyle = 'thin';
    return {
      top: { style: thin },
      bottom: { style: thin },
      left: { style: thin },
      right: { style: thin },
    };
  }

  private formatDateFrench(dateStr: string): string {
    const DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const MONTHS = [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre',
    ];
    const d = new Date(dateStr);
    return `${DAYS[d.getUTCDay()]} - ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  }

  private formatTimeRange(start?: string, end?: string): string {
    if (!start || !end) {
      return '';
    }
    const fmt = (t: string) => {
      const [h, m] = t.split(':');
      return `${parseInt(h)}h${m === '00' ? '' : m}`;
    };
    return `${fmt(start)} - ${fmt(end)}`;
  }

  formatHour(totalMinutes: number): string {
    const h = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, '0');
    const m = (totalMinutes % 60).toString().padStart(2, '0');
    return `${h}h${m}`;
  }

  splitBlocByTime(
    blocEntries: DailyDataEntry[],
    middleTime: string
  ): { morning: DailyDataEntry[]; afternoon: DailyDataEntry[] } {
    const midHour = this.parseHourStr(middleTime);

    const morning: DailyDataEntry[] = [];
    const afternoon: DailyDataEntry[] = [];

    for (const entry of blocEntries) {
      if (!entry.vacationLines || entry.vacationLines.length === 0) {
        morning.push(entry);
        afternoon.push(entry);
        continue;
      }

      const morningVacs: string[] = [];
      const afternoonVacs: string[] = [];
      const morningColors: (string | undefined)[] = [];
      const afternoonColors: (string | undefined)[] = [];
      const morningInter: { [groupId: string]: string }[] = [];
      const afternoonInter: { [groupId: string]: string }[] = [];

      entry.vacationLines.forEach((line, i) => {
        const timeMatch = line.match(/\((\d{1,2})h(\d{2})-(\d{1,2})h(\d{2})\)/);
        if (timeMatch) {
          const startHour = parseInt(timeMatch[1]) * 60 + parseInt(timeMatch[2]);
          const endHour = parseInt(timeMatch[3]) * 60 + parseInt(timeMatch[4]);

          const spansMidTime = startHour < midHour && endHour > midHour;
          const isAfternoonOnly = startHour >= midHour;

          if (spansMidTime) {
            const morningLine = line.replace(
              /\(\d{1,2}h\d{2}-\d{1,2}h\d{2}\)/,
              `(${this.formatHour(startHour)}-${this.formatHour(midHour)})`
            );
            const afternoonLine = line.replace(
              /\(\d{1,2}h\d{2}-\d{1,2}h\d{2}\)/,
              `(${this.formatHour(midHour)}-${this.formatHour(endHour)})`
            );

            morningVacs.push(morningLine);
            morningColors.push(entry.specialtyColors?.[i]);
            morningInter.push(entry.intervenantsPerGroupByVacation?.[i] ?? {});

            afternoonVacs.push(afternoonLine);
            afternoonColors.push(entry.specialtyColors?.[i]);
            afternoonInter.push(entry.intervenantsPerGroupByVacation?.[i] ?? {});
          } else if (isAfternoonOnly) {
            afternoonVacs.push(line);
            afternoonColors.push(entry.specialtyColors?.[i]);
            afternoonInter.push(entry.intervenantsPerGroupByVacation?.[i] ?? {});
          } else {
            morningVacs.push(line);
            morningColors.push(entry.specialtyColors?.[i]);
            morningInter.push(entry.intervenantsPerGroupByVacation?.[i] ?? {});
          }
        } else {
          morningVacs.push(line);
          morningColors.push(entry.specialtyColors?.[i]);
          morningInter.push(entry.intervenantsPerGroupByVacation?.[i] ?? {});
        }
      });

      if (morningVacs.length > 0) {
        morning.push({
          ...entry,
          vacationLines: morningVacs,
          specialtyColors: morningColors,
          intervenantsPerGroupByVacation: morningInter,
        });
      } else {
        morning.push({ ...entry, vacationLines: [], specialtyColors: [], intervenantsPerGroupByVacation: [] });
      }

      if (afternoonVacs.length > 0) {
        afternoon.push({
          ...entry,
          vacationLines: afternoonVacs,
          specialtyColors: afternoonColors,
          intervenantsPerGroupByVacation: afternoonInter,
        });
      } else {
        afternoon.push({ ...entry, vacationLines: [], specialtyColors: [], intervenantsPerGroupByVacation: [] });
      }
    }

    return { morning, afternoon };
  }

  parseHourStr(t: string): number {
    const m = t.match(/(\d{1,2})[h:](\d{0,2})/);
    if (!m) {
      return 780;
    }
    return parseInt(m[1]) * 60 + parseInt(m[2] || '0');
  }

  parseVacationColor(line: string): { cleanLine: string; color: string | undefined } {
    const match = line.match(/\[\[([#][0-9a-fA-F]{6})\]\]/);
    if (match) {
      return {
        cleanLine: line.replace(/\s*\[\[[#][0-9a-fA-F]{6}\]\]/, '').trim(),
        color: match[1],
      };
    }
    return { cleanLine: line, color: undefined };
  }

  hexToRgb1(hex: string): [number, number, number] {
    const clean = hex.replace('#', '');
    return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
  }

  mergeIntervenantsForEntry(
    entry: DailyDataEntry,
    groupId: string,
    halfFilter?: 'morning' | 'afternoon',
    midHour?: number
  ): string {
    if (!entry.intervenantsPerGroupByVacation) {
      return '';
    }
    const seen = new Set<string>();
    const lines: string[] = [];
    for (const perGroup of entry.intervenantsPerGroupByVacation) {
      const val = perGroup[groupId];
      if (val) {
        for (const line of val.split('\n')) {
          const trimmed = line.trim();
          if (!trimmed || seen.has(trimmed)) {
            continue;
          }

          if (halfFilter && midHour !== undefined) {
            const timeMatch = trimmed.match(/\((\d{1,2})h(\d{2})-(\d{1,2})h(\d{2})\)/);
            if (timeMatch) {
              const start = parseInt(timeMatch[1]) * 60 + parseInt(timeMatch[2]);
              const end = parseInt(timeMatch[3]) * 60 + parseInt(timeMatch[4]);
              if (halfFilter === 'morning' && start >= midHour) {
                continue;
              }
              if (halfFilter === 'afternoon' && end <= midHour) {
                continue;
              }
            }
          }

          seen.add(trimmed);
          lines.push(trimmed);
        }
      }
    }
    return lines.join('\n');
  }

  async saveDailySchedulingPDF1(
    dailyData: DailyDataEntry[],
    date: moment.Moment,
    roomsName: string,
    fileName: string,
    includeComments: boolean,
    intervenantColumns: IntervenantColumn[],
    selectedServicesNames: string,
    middleTime: string = '13:00',
    showRoles: boolean = false
  ): Promise<void> {
    try {
      const blocEntries = dailyData.filter((d) => d.section === 'BLOC');
      const horsEntries = dailyData.filter((d) => d.section === 'HORS_SALLE');
      const consultEntries = dailyData.filter((d) => d.section === 'CONSULTATION');
      const extraEntries = dailyData.filter((d) => d.section === 'EXTRA_CLINIQUE');
      const commentEntry = dailyData.find((d) => d.section === 'COMMENT');

      const { morning, afternoon } = this.splitBlocByTime(blocEntries, middleTime);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 5;
      const usableW = pageW - 2 * margin;

      const halfGap = 3;
      const halfW = (usableW - halfGap) / 2;

      const salleW = 28;
      const vacW = 42;
      const commentW = includeComments ? 32 : 0;
      const interTotalW = halfW - salleW - vacW - commentW;
      const numInterCols = intervenantColumns.length || 1;
      const interColW = interTotalW / numInterCols;

      const CELL_BG: [number, number, number] = [255, 255, 255];

      const buildColDefs = (startX: number) => {
        const cols: { label: string; x: number; w: number; key: string }[] = [];
        let cx = startX;

        cols.push({ label: roomsName || 'Salle', x: cx, w: salleW, key: 'salle' });
        cx += salleW;
        cols.push({ label: 'Vacations', x: cx, w: vacW, key: 'vacation' });
        cx += vacW;

        intervenantColumns.forEach((ic) => {
          cols.push({
            label: ic.header.toLowerCase() === 'anesthésiste' ? 'Anesthésie' : ic.header,
            x: cx,
            w: interColW,
            key: `inter_${ic.groupId}`,
          });
          cx += interColW;
        });

        if (includeComments) {
          cols.push({ label: 'Commentaire', x: cx, w: commentW, key: 'comment' });
        }

        return cols;
      };

      const drawHalfHeaders = (x: number, y: number, h: number, halfLabel: string) => {
        doc.setFillColor(...COLORS.headerBg);
        doc.rect(x, y, halfW, h, 'F');
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(x, y, halfW, h);
        doc.setTextColor(...COLORS.headerText);
        doc.setFontSize(FONT_SIZES.header + 0.5);
        doc.setFont('helvetica', 'bold');
        doc.text(halfLabel, x + halfW / 2, y + h / 2 + 1, { align: 'center' });

        const subY = y + h;
        const cols = buildColDefs(x);

        doc.setFillColor(...COLORS.subHeaderBg);
        doc.rect(x, subY, halfW, h, 'F');
        doc.setTextColor(...COLORS.subHeaderText);
        doc.setFontSize(FONT_SIZES.header);
        doc.setFont('helvetica', 'bold');

        cols.forEach((col) => {
          doc.text(col.label, col.x + col.w / 2, subY + h / 2 + 1, { align: 'center' });
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2);
          doc.line(col.x, subY, col.x, subY + h);
        });
        doc.line(x + halfW, subY, x + halfW, subY + h);
        doc.line(x, subY, x + halfW, subY);
        doc.line(x, subY + h, x + halfW, subY + h);
      };

      const drawBlocRow = (
        entry: DailyDataEntry,
        x: number,
        y: number,
        rowH: number,
        cols: ReturnType<typeof buildColDefs>
      ): number => {
        const vacLines = entry.vacationLines ?? [];
        const lineH = 6;

        const parsedVacs = vacLines.map((line) => this.parseVacationColor(line));
        const cleanVacLines = parsedVacs.map((p) => p.cleanLine);
        const vacText = cleanVacLines.join('\n') || '-';

        const interTexts: { [key: string]: string } = {};
        intervenantColumns.forEach((ic) => {
          interTexts[`inter_${ic.groupId}`] = this.mergeIntervenantsForEntry(entry, ic.groupId);
        });

        const vacCount = vacText.split('\n').length;
        const maxLines = Math.max(
          vacCount,
          ...Object.values(interTexts).map((t) => t.split('\n').filter(Boolean).length),
          1
        );
        const actualH = Math.max(rowH, maxLines * lineH + 3);

        doc.setFillColor(...CELL_BG);
        doc.rect(
          x,
          y,
          cols.reduce((s, c) => s + c.w, 0),
          actualH,
          'F'
        );

        doc.setFillColor(235, 235, 235);
        doc.rect(x, y, salleW, actualH, 'F');

        doc.setFontSize(FONT_SIZES.cell + 2);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);

        cols.forEach((col, _) => {
          if (col.key === 'salle') {
            const text = entry.salle ?? '';
            const wrapped = doc.splitTextToSize(text, col.w - 1.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(wrapped, col.x + col.w / 2, y + actualH / 2, { align: 'center', baseline: 'middle' });
          } else if (col.key === 'vacation') {
            let lineY = y;
            const vacHeights = parsedVacs.map((pv) => {
              const wrappedLine = doc.splitTextToSize(pv.cleanLine || '—', col.w - 1.5);
              return wrappedLine.length * lineH + 1;
            });
            const totalVacH = vacHeights.reduce((s, h) => s + h, 0);
            const scale = totalVacH > 0 ? actualH / totalVacH : 1;

            parsedVacs.forEach((pv, pvIdx) => {
              const lineText = pv.cleanLine || '-';
              const wrappedLine = doc.splitTextToSize(pv.cleanLine, col.w - 1.5);
              const singleLineH = vacHeights[pvIdx] * scale;

              const colorHex = pv.color ? undefined : entry.specialtyColors?.[pvIdx];
              if (colorHex) {
                const [r, g, b] = this.hexToRgb1(colorHex);
                const lr = Math.round(r + (255 - r) * 0.45);
                const lg = Math.round(g + (255 - g) * 0.45);
                const lb = Math.round(b + (255 - b) * 0.45);
                doc.setFillColor(lr, lg, lb);
                doc.rect(col.x, lineY, col.w, singleLineH, 'F');
              }
              doc.setTextColor(0, 0, 0);
              doc.setFont('helvetica', 'bold');

              const timeMatch = lineText.match(/\((\d{1,2}h\d{2}-\d{1,2}h\d{2})\)/);
              if (timeMatch && pv.color) {
                const [r, g, b] = this.hexToRgb1(pv.color);
                const beforeTime = lineText.substring(0, lineText.indexOf(timeMatch[0]));
                const timeStr = timeMatch[0];
                const afterTime = lineText.substring(lineText.indexOf(timeMatch[0]) + timeMatch[0].length);

                const beforeW = doc.getTextWidth(beforeTime);
                const timeW = doc.getTextWidth(timeStr);
                const afterW = doc.getTextWidth(afterTime);
                const totalTextW = beforeW + timeW + afterW;

                if (totalTextW <= col.w - 1.5) {
                  const startX = col.x + (col.w - totalTextW) / 2;
                  doc.setFillColor(r, g, b);
                  doc.rect(startX + beforeW, lineY, timeW, singleLineH, 'F');
                  doc.setTextColor(0, 0, 0);
                  doc.text(beforeTime, startX, lineY + 1.5, { baseline: 'top' });
                  doc.text(timeStr, startX + beforeW, lineY + 1.5, { baseline: 'top' });
                  doc.text(afterTime, startX + beforeW + timeW, lineY + 1.5, { baseline: 'top' });
                } else {
                  const wrappedBefore = doc.splitTextToSize(beforeTime, col.w - 1.5);

                  wrappedBefore.forEach((wLine: string) => {
                    doc.setTextColor(0, 0, 0);
                    doc.text(wLine, col.x + col.w / 2, lineY + 1.5, { align: 'center', baseline: 'top' });
                    lineY += lineH;
                  });

                  const timeLineW = timeW + afterW;
                  const timeStartX = col.x + (col.w - timeLineW) / 2;
                  doc.setFillColor(r, g, b);
                  doc.rect(timeStartX, lineY, timeW, lineH, 'F');
                  doc.setTextColor(0, 0, 0);
                  doc.text(timeStr, timeStartX, lineY + 1.5, { baseline: 'top' });
                  doc.text(afterTime, timeStartX + timeW, lineY + 1.5, { baseline: 'top' });
                }
              } else {
                doc.text(wrappedLine, col.x + col.w / 2, lineY + 1.5, { align: 'center', baseline: 'top' });
              }

              lineY += singleLineH;
            });
            if (parsedVacs.length === 0) {
              doc.setTextColor(0, 0, 0);
              doc.text('—', col.x + col.w / 2, y + actualH / 2, { align: 'center', baseline: 'middle' });
            }
          } else if (col.key === 'comment') {
            const html = entry.commentPerRoom ?? '';
            if (html) {
              this.drawHtmlComment(doc, html, col.x, y, col.w - 1.5);
            }
          } else if (col.key.startsWith('inter_')) {
            const text = interTexts[col.key] ?? '';
            if (text) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(FONT_SIZES.cell);
              let lineOffsetY = y + 2.5;
              const personLines = text.split('\n');
              personLines.forEach((personLine) => {
                const { cleanLine, color } = this.parseVacationColor(personLine);
                const timeMatch = cleanLine.match(/\(([^)]+)\)/);

                if (timeMatch && color) {
                  const [r, g, b] = this.hexToRgb1(color);
                  const beforeTime = cleanLine.substring(0, cleanLine.indexOf(timeMatch[0]));
                  const timeStr = timeMatch[0];
                  const afterTime = cleanLine.substring(cleanLine.indexOf(timeMatch[0]) + timeMatch[0].length);

                  const beforeW = doc.getTextWidth(beforeTime);
                  const timeW = doc.getTextWidth(timeStr);
                  const afterW = doc.getTextWidth(afterTime);
                  const totalTextW = beforeW + timeW + afterW;

                  if (totalTextW <= col.w - 1.5) {
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(showRoles ? FONT_SIZES.cell + 2 : FONT_SIZES.cell + 4);
                    const nameW = doc.getTextWidth(beforeTime + ' ');

                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(showRoles ? FONT_SIZES.cell - 2 : FONT_SIZES.cell - 1);
                    const timeStrW = doc.getTextWidth(timeStr);

                    doc.setFillColor(r, g, b);
                    doc.rect(col.x + 0.8 + nameW, lineOffsetY - 0.5, timeStrW, lineH - 2, 'F');

                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(showRoles ? FONT_SIZES.cell + 2 : FONT_SIZES.cell + 4);
                    doc.setTextColor(0, 0, 0);
                    doc.text(beforeTime, col.x + 0.8, lineOffsetY, { baseline: 'top' });

                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(showRoles ? FONT_SIZES.cell - 2 : FONT_SIZES.cell - 1);
                    doc.text(timeStr, col.x + 0.8 + nameW, lineOffsetY + 0.4, { baseline: 'top' });
                    doc.text(afterTime, col.x + 0.8 + nameW + timeStrW, lineOffsetY + 0.4, { baseline: 'top' });

                    lineOffsetY += lineH;
                  } else {
                    const wrappedBefore = doc.splitTextToSize(beforeTime, col.w - 1.5);
                    wrappedBefore.forEach((wLine: string) => {
                      doc.setTextColor(0, 0, 0);
                      doc.text(wLine, col.x + 0.8, lineOffsetY, { baseline: 'top' });
                      lineOffsetY += lineH;
                    });
                    doc.setFillColor(r, g, b);
                    doc.rect(col.x + 0.8, lineOffsetY - 0.5, timeW, lineH - 2, 'F');
                    doc.setTextColor(0, 0, 0);
                    doc.text(timeStr, col.x + 0.8, lineOffsetY + 0.4, { baseline: 'top' });
                    doc.text(afterTime, col.x + 0.8 + timeW, lineOffsetY + 0.4, { baseline: 'top' });
                    lineOffsetY += lineH;
                  }
                } else {
                  const nameMatch = cleanLine.match(/^([^(]+)/);
                  const namePart = nameMatch ? nameMatch[1].trim() : cleanLine;
                  const restPart = cleanLine.substring(namePart.length).trim();

                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(showRoles ? FONT_SIZES.cell + 2 : FONT_SIZES.cell + 4);
                  const nameW = doc.getTextWidth(namePart + ' ');

                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(FONT_SIZES.cell - 1);
                  const restW = doc.getTextWidth(restPart);
                  const totalW = nameW + restW;

                  if (totalW <= col.w - 1.5) {
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(showRoles ? FONT_SIZES.cell + 2 : FONT_SIZES.cell + 4);
                    doc.setTextColor(0, 0, 0);
                    doc.text(namePart, col.x + 0.8, lineOffsetY, { baseline: 'top' });

                    if (restPart) {
                      doc.setFont('helvetica', 'normal');
                      doc.setFontSize(showRoles ? FONT_SIZES.cell - 1 : FONT_SIZES.cell);
                      doc.text(restPart, col.x + 0.8 + nameW, lineOffsetY + 0.4, { baseline: 'top' });
                    }
                    lineOffsetY += lineH;
                  } else {
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(showRoles ? FONT_SIZES.cell + 2 : FONT_SIZES.cell + 4);
                    doc.setTextColor(0, 0, 0);
                    doc.text(namePart, col.x + 0.8, lineOffsetY, { baseline: 'top' });
                    lineOffsetY += lineH;

                    if (restPart) {
                      doc.setFont('helvetica', 'normal');
                      doc.setFontSize(showRoles ? FONT_SIZES.cell - 1 : FONT_SIZES.cell);
                      const wrappedRest = doc.splitTextToSize(restPart, col.w - 1.5);
                      wrappedRest.forEach((wLine: string) => {
                        doc.text(wLine, col.x + 0.8, lineOffsetY, { baseline: 'top' });
                        lineOffsetY += lineH;
                      });
                    }
                  }
                }
              });
            }
          }
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(FONT_SIZES.cell);
          doc.setTextColor(0, 0, 0);

          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2);
          doc.rect(col.x, y, col.w, actualH);
        });

        const totalW = cols.reduce((s, c) => s + c.w, 0);
        doc.setDrawColor(0, 0, 0);
        doc.line(x + totalW, y, x + totalW, y + actualH);

        return actualH;
      };

      let curY = margin;

      doc.setFillColor(...COLORS.headerBg);
      doc.rect(margin, curY, usableW, 8, 'F');
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.rect(margin, curY, usableW, 8);
      doc.setTextColor(...COLORS.headerText);
      doc.setFontSize(FONT_SIZES.title);
      doc.setFont('helvetica', 'bold');
      const titleText = `RÉPARTITION ${selectedServicesNames.toUpperCase()}  ${date.format('dddd DD MMMM YYYY').toUpperCase()}`;
      doc.text(titleText, pageW / 2, curY + 5.5, { align: 'center' });
      curY += 9;

      const headerH = 7;
      const xLeft = margin;
      const xRight = margin + halfW + halfGap;

      drawHalfHeaders(xLeft, curY, headerH, 'MATIN');
      drawHalfHeaders(xRight, curY, headerH, 'APRÈS-MIDI');
      curY += headerH * 2;

      const leftCols = buildColDefs(xLeft);
      const rightCols = buildColDefs(xRight);

      const maxRooms = Math.max(morning.length, afternoon.length);
      const baseRowH = 10;

      const measureH = (entry: DailyDataEntry | undefined): number => {
        if (!entry) {
          return baseRowH;
        }
        const lineH = 8;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(FONT_SIZES.cell);
        const salleLines = doc.splitTextToSize(entry.salle ?? '', salleW - 1.5).length;

        const vacLines =
          (entry.vacationLines ?? [])
            .map((line) => {
              const { cleanLine } = this.parseVacationColor(line);
              return doc.splitTextToSize(cleanLine, vacW - 1.5).length;
            })
            .reduce((s, n) => s + n, 0) || 1;

        const interLines = intervenantColumns.map((ic) => {
          const text = this.mergeIntervenantsForEntry(entry, ic.groupId);
          return text
            .split('\n')
            .filter(Boolean)
            .map((line) => {
              const { cleanLine } = this.parseVacationColor(line);
              return doc.splitTextToSize(cleanLine, interColW - 1.5).length;
            })
            .reduce((s, n) => s + n, 0);
        });
        const commentLines =
          includeComments && entry.commentPerRoom
            ? Math.ceil(this.measureHtmlComment(doc, entry.commentPerRoom, commentW - 1.5) / lineH)
            : 0;

        const maxLines = Math.max(salleLines, vacLines, ...interLines, commentLines, 1);
        return Math.max(baseRowH, maxLines * lineH + 3);
      };

      for (let i = 0; i < maxRooms; i++) {
        const mEntry = morning[i];
        const aEntry = afternoon[i];
        const rowH = Math.max(measureH(mEntry), measureH(aEntry));

        if (curY + rowH > pageH - margin - 15) {
          doc.addPage();
          curY = margin;
          drawHalfHeaders(xLeft, curY, headerH, 'MATIN (suite)');
          drawHalfHeaders(xRight, curY, headerH, 'APRÈS-MIDI (suite)');
          curY += headerH * 2;
        }

        if (mEntry) {
          drawBlocRow(mEntry, xLeft, curY, rowH, leftCols);
        } else {
          doc.setFillColor(250, 250, 250);
          doc.rect(xLeft, curY, halfW, rowH, 'F');
        }

        if (aEntry) {
          drawBlocRow(aEntry, xRight, curY, rowH, rightCols);
        } else {
          doc.setFillColor(250, 250, 250);
          doc.rect(xRight, curY, halfW, rowH, 'F');
        }

        curY += rowH;
      }

      curY += 3;

      if (horsEntries.length > 0) {
        if (curY + 12 > pageH - margin) {
          doc.addPage();
          curY = margin;
        }

        doc.setFillColor(...COLORS.sectionBg);
        doc.rect(margin, curY, usableW, 8, 'F');
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(margin, curY, usableW, 8);
        doc.setFontSize(FONT_SIZES.header);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.headerBg);
        doc.text('HORS SALLE', margin + 2, curY + 5);
        curY += 6;

        horsEntries.forEach((entry) => {
          const rowH = 6;

          if (curY + rowH > pageH - margin) {
            doc.addPage();
            curY = margin;
          }

          doc.setFillColor(...COLORS.horsAlleBg);
          doc.rect(margin, curY, usableW, rowH, 'F');

          doc.setFontSize(FONT_SIZES.cell);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          const horsLabelW = 50;

          doc.text(entry.label ?? '', margin + 1, curY + rowH / 2, { baseline: 'middle' });

          const commentText = entry.comment ?? '';
          const persons = commentText
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean);
          let offsetX = margin + horsLabelW + 4;
          const offsetY = curY + rowH / 2;

          persons.forEach((person, idx) => {
            const nameMatchH = person.match(/^([^(]+)/);
            const namePartH = nameMatchH ? nameMatchH[1].trim() : person;
            const restPartH = person.substring(namePartH.length).trim();
            const suffix = idx < persons.length - 1 ? ', ' : '';

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(FONT_SIZES.cell + 2);
            doc.setTextColor(0, 0, 0);
            const nameWH = doc.getTextWidth(namePartH + ' ');
            doc.text(namePartH, offsetX, offsetY, { baseline: 'middle' });
            offsetX += nameWH;

            if (restPartH) {
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(FONT_SIZES.cell - 2);
              const restWithSuffix = restPartH + suffix;
              const restWH = doc.getTextWidth(restWithSuffix + ' ');
              doc.text(restWithSuffix, offsetX, offsetY + 0.2, { baseline: 'middle' });
              offsetX += restWH;
            }
          });
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2);
          doc.rect(margin, curY, usableW, rowH);
          doc.line(margin + horsLabelW, curY, margin + horsLabelW, curY + rowH);

          curY += rowH;
        });

        curY += 2;
      }

      const otherEntries = [...consultEntries, ...extraEntries];
      if (otherEntries.length > 0) {
        if (curY + 10 > pageH - margin) {
          doc.addPage();
          curY = margin;
        }

        doc.setFillColor(...COLORS.sectionBg);
        doc.rect(margin, curY, usableW, 8, 'F');
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(margin, curY, usableW, 8);
        doc.setFontSize(FONT_SIZES.header);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.headerBg);
        doc.text('CONSULTATION / EXTRACLINIQUE', margin + 2, curY + 5);
        curY += 6;
        otherEntries.forEach((entry) => {
          const rowH = 6;
          if (curY + rowH > pageH - margin) {
            doc.addPage();
            curY = margin;
          }

          doc.setFillColor(255, 255, 255);
          doc.rect(margin, curY, usableW, rowH, 'F');

          doc.setFontSize(FONT_SIZES.cell);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          const label = entry.section === 'CONSULTATION' ? 'Consultation' : (entry.salle ?? 'Extra-clinique');
          doc.text(label, margin + 1, curY + rowH / 2, { baseline: 'middle' });
          const anesthText = entry.anesth ?? '';
          if (anesthText) {
            const persons = anesthText
              .split(',')
              .map((p) => p.trim())
              .filter(Boolean);
            let offsetX = margin + 52;
            const offsetY = curY + rowH / 2;

            persons.forEach((person, idx) => {
              const nameMatchA = person.match(/^([^(]+)/);
              const namePartA = nameMatchA ? nameMatchA[1].trim() : person;
              const restPartA = person.substring(namePartA.length).trim();
              const suffix = idx < persons.length - 1 ? ', ' : '';

              doc.setFont('helvetica', 'bold');
              doc.setFontSize(FONT_SIZES.cell + 2);
              doc.setTextColor(0, 0, 0);
              const nameWA = doc.getTextWidth(namePartA + ' ');
              doc.text(namePartA, offsetX, offsetY, { baseline: 'middle' });
              offsetX += nameWA;

              if (restPartA) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(FONT_SIZES.cell - 2);
                const restWithSuffix = restPartA + suffix;
                const restWA = doc.getTextWidth(restWithSuffix + ' ');
                doc.text(restWithSuffix, offsetX, offsetY + 0.2, { baseline: 'middle' });
                offsetX += restWA;
              }
            });
          }
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2);
          doc.rect(margin, curY, usableW, rowH);
          doc.line(margin + 50, curY, margin + 50, curY + rowH);

          curY += rowH;
        });

        curY += 2;
      }

      if (includeComments && commentEntry?.comment) {
        if (curY + 10 > pageH - margin) {
          doc.addPage();
          curY = margin;
        }

        doc.setFillColor(...COLORS.commentBg);
        const commentBlockH = Math.max(8, this.measureHtmlComment(doc, commentEntry.comment, usableW - 4) + 3);
        doc.setFillColor(...COLORS.commentBg);
        doc.rect(margin, curY, usableW, commentBlockH, 'F');
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(margin, curY, usableW, commentBlockH);
        this.drawHtmlComment(doc, commentEntry.comment, margin, curY, usableW - 4);
      }

      const totalPages = (doc.internal as any).getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(FONT_SIZES.small);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120, 120, 120);
        doc.text(`${p} / ${totalPages}`, pageW - margin, pageH - 2, { align: 'right' });
      }

      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('Error saving daily PDF file:', error);
      throw error;
    }
  }

  async exportMotifsClosureExcel(
    motifs: any[],
    fileName: string,
    selectedSpecialties: { id: string; name: string }[],
    hospitalNames: { surgeonsName: string; specialtiesName: string }
  ): Promise<void> {
    const workbook = new Excel.Workbook();

    const MOTIF_BG = 'FF1E3A5F';
    const MOTIF_FG = 'FFFFFFFF';
    const HEADER_BG = 'FFD9E1F2';
    const THIN: Excel.Border = { style: 'thin', color: { argb: 'FF000000' } };
    const FULL_BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

    const setColWidths = (ws: Excel.Worksheet) => {
      ws.getColumn(1).width = 35;
      ws.getColumn(2).width = 14;
      ws.getColumn(3).width = 14;
    };

    const addTitleRow = (ws: Excel.Worksheet, text: string) => {
      const row = ws.addRow([text, '', '']);
      ws.mergeCells(row.number, 1, row.number, 3);
      const cell = row.getCell(1);
      cell.font = { bold: true, color: { argb: MOTIF_FG } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: MOTIF_BG } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = FULL_BORDER;
      row.height = 20;
    };

    const addHeaderRow = (ws: Excel.Worksheet) => {
      const row = ws.addRow(['Motif', 'Fermertures', 'Durée']);
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = FULL_BORDER;
      });
    };

    const addTotalRow = (ws: Excel.Worksheet, value: number, duration: string) => {
      const row = ws.addRow(['Total', value, duration]);
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = FULL_BORDER;
      });
    };

    const addDataRow = (ws: Excel.Worksheet, label: string, value: number, duration: string) => {
      const row = ws.addRow([label || '—', value, duration]);
      row.eachCell((cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = FULL_BORDER;
      });
    };

    const parseDurationToMinutes = (duration: string): number => {
      if (!duration) {
        return 0;
      }
      const hoursMatch = duration.match(/(\d+)\s*h/);
      const minutesMatch = duration.match(/(\d+)\s*min/);
      const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
      const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
      return hours * 60 + minutes;
    };

    const formatMinutes = (totalMin: number): string => {
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      return `${h} h ${String(m).padStart(2, '0')} min`;
    };

    // --- Sheet 1: Global ---
    const globalWs = workbook.addWorksheet('Global');
    setColWidths(globalWs);
    addTitleRow(globalWs, 'Global');
    addHeaderRow(globalWs);

    let globalTotalValue = 0;
    let globalTotalMinutes = 0;

    const globalMotifRows = motifs.map((motif) => {
      const surgeons: any[] = motif.surgeons ?? [];
      const value = surgeons.reduce((sum: number, s: any) => sum + (s.value ?? 0), 0);
      const minutes = surgeons.reduce((sum: number, s: any) => sum + parseDurationToMinutes(s.duration ?? ''), 0);
      globalTotalValue += value;
      globalTotalMinutes += minutes;
      return { label: motif.label ?? '', value, minutes };
    });

    addTotalRow(globalWs, globalTotalValue, formatMinutes(globalTotalMinutes));
    for (const row of globalMotifRows) {
      addDataRow(globalWs, row.label, row.value, formatMinutes(row.minutes));
    }

    // --- Sheet 2 ---
    const specialtyWs = workbook.addWorksheet(`Par ${hospitalNames.specialtiesName.toLowerCase()}`);
    setColWidths(specialtyWs);

    let firstSpecialtyWritten = true;
    for (const specialty of selectedSpecialties) {
      let specTotalValue = 0;
      let specTotalMinutes = 0;

      const specMotifRows = motifs.map((motif) => {
        const surgeons: any[] = (motif.surgeons ?? []).filter((s: any) =>
          (s.profile?.specialties ?? []).some((sp: any) => String(sp._id ?? sp) === specialty.id)
        );
        const value = surgeons.reduce((sum: number, s: any) => sum + (s.value ?? 0), 0);
        const minutes = surgeons.reduce((sum: number, s: any) => sum + parseDurationToMinutes(s.duration ?? ''), 0);
        specTotalValue += value;
        specTotalMinutes += minutes;
        return { label: motif.label ?? '', value, minutes };
      });

      if (specTotalValue === 0 && specTotalMinutes === 0) {
        continue;
      }

      if (!firstSpecialtyWritten) {
        specialtyWs.addRow([]);
      }
      firstSpecialtyWritten = false;

      addTitleRow(specialtyWs, specialty.name);
      addHeaderRow(specialtyWs);
      addTotalRow(specialtyWs, specTotalValue, formatMinutes(specTotalMinutes));
      for (const row of specMotifRows.filter((r) => r.value > 0)) {
        addDataRow(specialtyWs, row.label, row.value, formatMinutes(row.minutes));
      }
    }

    // --- Sheet 3 ---
    const surgeonWs = workbook.addWorksheet(`Par ${hospitalNames.surgeonsName.toLowerCase()}`);
    setColWidths(surgeonWs);

    const surgeonMap = new Map<
      string,
      { name: string; motifEntries: { label: string; value: number; minutes: number }[] }
    >();

    for (const motif of motifs) {
      for (const surgeon of motif.surgeons ?? []) {
        const key: string =
          surgeon.profile?._id ?? `${surgeon.profile?.firstName ?? ''} ${surgeon.profile?.lastName ?? ''}`.trim();
        if (!surgeonMap.has(key)) {
          const firstName: string = surgeon.profile?.firstName ?? '';
          const lastName: string = surgeon.profile?.lastName ?? '';
          surgeonMap.set(key, { name: `${firstName} ${lastName}`.trim() || '—', motifEntries: [] });
        }
        surgeonMap.get(key)!.motifEntries.push({
          label: motif.label ?? '',
          value: surgeon.value ?? 0,
          minutes: parseDurationToMinutes(surgeon.duration ?? ''),
        });
      }
    }

    let firstSurgeon = true;
    for (const [, surgeonData] of surgeonMap) {
      if (!firstSurgeon) {
        surgeonWs.addRow([]);
      }
      firstSurgeon = false;

      addTitleRow(surgeonWs, surgeonData.name);
      addHeaderRow(surgeonWs);

      const totalValue = surgeonData.motifEntries.reduce((sum, m) => sum + m.value, 0);
      const totalMinutes = surgeonData.motifEntries.reduce((sum, m) => sum + m.minutes, 0);

      addTotalRow(surgeonWs, totalValue, formatMinutes(totalMinutes));
      for (const entry of surgeonData.motifEntries) {
        addDataRow(surgeonWs, entry.label, entry.value, formatMinutes(entry.minutes));
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    this.save(buffer, `${fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }

  parseHtmlToStyledLines(html: string): StyledLine[] {
    if (!html) {
      return [];
    }

    const normalised = html
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"');

    const lines: StyledLine[] = [];
    let currentLine: StyledLine = [];

    const boldStack: boolean[] = [false];
    const italicStack: boolean[] = [false];
    const colorStack: [number, number, number][] = [[0, 0, 0]];

    const pushRun = (text: string) => {
      if (!text) {
        return;
      }
      currentLine.push({
        text,
        bold: boldStack[boldStack.length - 1],
        italic: italicStack[italicStack.length - 1],
        color: colorStack[colorStack.length - 1],
      });
    };

    const flushLine = () => {
      const merged: StyledRun[] = [];
      for (const run of currentLine) {
        const last = merged[merged.length - 1];
        if (
          last &&
          last.bold === run.bold &&
          last.italic === run.italic &&
          last.color[0] === run.color[0] &&
          last.color[1] === run.color[1] &&
          last.color[2] === run.color[2]
        ) {
          last.text += run.text;
        } else {
          merged.push({ ...run });
        }
      }
      if (merged.length > 0) {
        lines.push(merged);
      }
      currentLine = [];
    };

    const tokenRe = /(<[^>]+>|[^<]+)/g;
    let match: RegExpExecArray | null;

    while ((match = tokenRe.exec(normalised)) !== null) {
      const token = match[1];

      if (token.startsWith('<')) {
        const tagContent = token.slice(1, -1).trim();
        const isClose = tagContent.startsWith('/');
        const tagName = isClose
          ? tagContent.slice(1).split(/\s/)[0].toLowerCase()
          : tagContent.split(/[\s/]/)[0].toLowerCase();

        if (isClose) {
          switch (tagName) {
            case 'strong':
            case 'b':
              if (boldStack.length > 1) {
                boldStack.pop();
              }
              break;
            case 'em':
            case 'i':
              if (italicStack.length > 1) {
                italicStack.pop();
              }
              break;
            case 'span':
              if (colorStack.length > 1) {
                colorStack.pop();
              }
              break;
            case 'p':
            case 'div':
            case 'br':
              flushLine();
              break;
          }
        } else {
          switch (tagName) {
            case 'strong':
            case 'b':
              boldStack.push(true);
              break;
            case 'em':
            case 'i':
              italicStack.push(true);
              break;
            case 'br':
              flushLine();
              break;
            case 'p':
            case 'div':
              if (currentLine.length > 0) {
                flushLine();
              }
              break;
            case 'span': {
              // Extract color from style attribute: color: rgb(r, g, b) or #hex
              const styleMatch = tagContent.match(/style\s*=\s*["']([^"']*)["']/i);
              let color: [number, number, number] = colorStack[colorStack.length - 1];
              if (styleMatch) {
                const style = styleMatch[1];
                const rgbMatch = style.match(/color\s*:\s*rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
                const hexMatch = style.match(/color\s*:\s*#([0-9a-f]{3,6})/i);
                if (rgbMatch) {
                  color = [+rgbMatch[1], +rgbMatch[2], +rgbMatch[3]];
                } else if (hexMatch) {
                  color = this.hexToRgbTuple(hexMatch[1]);
                }
              }
              colorStack.push(color);
              break;
            }
          }

          // Also handle inline style on <strong> / <b> for colour
          if ((tagName === 'strong' || tagName === 'b') && !isClose) {
            const styleMatch = tagContent.match(/style\s*=\s*["']([^"']*)["']/i);
            if (styleMatch) {
              const style = styleMatch[1];
              const rgbMatch = style.match(/color\s*:\s*rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
              const hexMatch = style.match(/color\s*:\s*#([0-9a-f]{3,6})/i);
              if (rgbMatch) {
                colorStack.push([+rgbMatch[1], +rgbMatch[2], +rgbMatch[3]]);
              } else if (hexMatch) {
                colorStack.push(this.hexToRgbTuple(hexMatch[1]));
              } else {
                colorStack.push(colorStack[colorStack.length - 1]);
              }
            } else {
              colorStack.push(colorStack[colorStack.length - 1]);
            }
          }
        }
      } else {
        pushRun(token);
      }
    }

    if (currentLine.length > 0) {
      flushLine();
    }

    return lines.filter((l) => l.some((r) => r.text.trim().length > 0));
  }

  hexToRgbTuple(hex: string): [number, number, number] {
    let h = hex.replace('#', '');
    if (h.length === 3) {
      h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    }
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  measureHtmlComment(doc: any, html: string, maxW: number, lineH = 4.5, baseFontSize = 7): number {
    const styledLines = this.parseHtmlToStyledLines(html);
    if (styledLines.length === 0) {
      return 0;
    }

    let totalH = 0;
    for (const line of styledLines) {
      const wrappedCount = this.countWrappedLines(doc, line, maxW, baseFontSize);
      totalH += wrappedCount * lineH;
    }
    return totalH + 2;
  }

  countWrappedLines(doc: any, runs: StyledRun[], maxW: number, baseFontSize: number): number {
    const fullText = runs.map((r) => r.text).join('');
    doc.setFontSize(baseFontSize);
    doc.setFont('helvetica', 'normal');
    const wrapped = doc.splitTextToSize(fullText, maxW);
    return Math.max(1, wrapped.length);
  }

  drawHtmlComment(doc: any, html: string, x: number, y: number, maxW: number, lineH = 4.5, baseFontSize = 7): number {
    const styledLines = this.parseHtmlToStyledLines(html);
    if (styledLines.length === 0) {
      return 0;
    }

    let curY = y + 1.5;

    for (const line of styledLines) {
      const wordTokens = this.buildWordTokens(doc, line, baseFontSize);
      const visualLines = this.reflowTokens(wordTokens, maxW);

      for (const visualLine of visualLines) {
        let curX = x + 0.8;

        for (const seg of visualLine) {
          this.applyRunStyle(doc, seg.run, baseFontSize);
          doc.setTextColor(seg.run.color[0], seg.run.color[1], seg.run.color[2]);
          doc.text(seg.text, curX, curY, { baseline: 'top' });
          curX += doc.getTextWidth(seg.text);
        }

        curY += lineH;
      }
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(baseFontSize);
    doc.setTextColor(0, 0, 0);

    return curY - y;
  }

  applyRunStyle(doc: any, run: StyledRun, baseFontSize: number) {
    const style = run.bold && run.italic ? 'bolditalic' : run.bold ? 'bold' : run.italic ? 'italic' : 'normal';
    doc.setFont('helvetica', style);
    doc.setFontSize(run.fontSize ?? baseFontSize);
  }

  buildWordTokens(doc: any, runs: StyledRun[], baseFontSize: number): WordToken[] {
    const tokens: WordToken[] = [];

    for (const run of runs) {
      const words = run.text.split(/(\s+)/);
      for (const word of words) {
        if (!word) {
          continue;
        }
        this.applyRunStyle(doc, run, baseFontSize);
        const width = doc.getTextWidth(word);
        tokens.push({ text: word, run, width });
      }
    }

    return tokens;
  }

  reflowTokens(tokens: WordToken[], maxW: number): VisualSegment[][] {
    const lines: VisualSegment[][] = [];
    let currentLine: VisualSegment[] = [];
    let currentW = 0;

    for (const token of tokens) {
      const isWhitespace = /^\s+$/.test(token.text);

      if (isWhitespace) {
        if (currentLine.length > 0) {
          const last = currentLine[currentLine.length - 1];
          last.text += token.text;
          currentW += token.width;
        }
        continue;
      }

      if (currentW + token.width > maxW && currentLine.length > 0) {
        const last = currentLine[currentLine.length - 1];
        last.text = last.text.trimEnd();
        lines.push(currentLine);
        currentLine = [];
        currentW = 0;
      }

      currentLine.push({ text: token.text, run: token.run });
      currentW += token.width;
    }

    if (currentLine.length > 0) {
      const last = currentLine[currentLine.length - 1];
      last.text = last.text.trimEnd();
      lines.push(currentLine);
    }

    return lines;
  }

  async exportCoconsPlanningExcel(data: any[], fileName: string): Promise<void> {
    try {
      const workbook = new ExcelJS.Workbook();

      const sheetMap = new Map<string, Room[]>();

      for (const room of data) {
        for (const sched of room.schedules) {
          const date = sched.date.slice(0, 10);

          if (!sheetMap.has(date)) {
            sheetMap.set(date, []);
          }

          if (!sheetMap.get(date)!.includes(room)) {
            sheetMap.get(date)!.push(room);
          }
        }
      }

      for (const [date, rooms] of sheetMap.entries()) {
        const sheetName = this.formatDateShort(date);
        const safeName = this.sanitizeSheetName(sheetName);

        const ws = workbook.addWorksheet(safeName, {
          views: [{ state: 'frozen', ySplit: 2, showGridLines: false }],
        });

        this.populateCoconsSheet(ws, rooms, date);
      }

      if (sheetMap.size === 0) {
        const ws = workbook.addWorksheet('Vide');
        ws.addRow(['Aucune donnée']);
      }

      const buffer = await workbook.xlsx.writeBuffer();

      this.save(buffer, `${fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    } catch (e) {
      console.error('Error exporting cocons planning:', e);
      throw e;
    }
  }

  private populateCoconsSheet(ws: ExcelJS.Worksheet, rooms: any[], date: string): void {
    const TIME_COL = 1;
    const ROW_OFFSET = 3;
    const SLOT_HEIGHT = 20;

    const sortedRooms = [...rooms].sort(
      (a, b) =>
        a.recoveryRoom.priority - b.recoveryRoom.priority ||
        a.recoveryRoom.roomNumber.localeCompare(b.recoveryRoom.roomNumber)
    );

    const { slots: TIME_SLOTS, minMinutes } = this.buildTimeSlots(sortedRooms, date);

    const occupied = new Map<number, Set<number>>();

    //  Columns
    ws.getColumn(TIME_COL).width = 7;

    sortedRooms.forEach((_, i) => {
      const col = TIME_COL + 1 + i * 2;
      ws.getColumn(col).width = 18;
      ws.getColumn(col + 1).width = 2;
    });

    const lastCol = TIME_COL + sortedRooms.length * 2;

    //  Title
    ws.mergeCells(1, 1, 1, lastCol);
    const title = ws.getCell(1, 1);
    title.value = `Date : ${this.formatDateShort(date)}`;
    title.font = { bold: true, size: 14 };
    title.alignment = { horizontal: 'center', vertical: 'middle' };
    title.border = {
      top: { style: undefined },
      bottom: { style: undefined },
      left: { style: undefined },
      right: { style: undefined },
    };
    ws.getRow(1).height = 25;

    for (let c = 1; c <= lastCol; c++) {
      ws.getCell(1, c).border = {
        top: { style: undefined },
        bottom: { style: undefined },
        left: { style: undefined },
        right: { style: undefined },
      };
    }

    //  Headers
    sortedRooms.forEach((room, i) => {
      const col = TIME_COL + 1 + i * 2;
      const spacerCol = col + 1;

      const cell = ws.getCell(2, col);
      cell.value = `Cocon ${room.recoveryRoom.roomNumber}`;
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = this.fill(room.recoveryRoom?.color ? this.hexToARGB(room.recoveryRoom.color) : 'FF92D050');
      cell.border = {
        top: { style: 'medium' },
        bottom: { style: 'medium' },
        left: { style: 'medium' },
        right: { style: 'medium' },
      };

      const spacer = ws.getCell(2, spacerCol);
      spacer.value = null;
      spacer.fill = this.fill('FFFFFFFF');
      spacer.border = {
        top: { style: undefined },
        bottom: { style: undefined },
        left: { style: undefined },
        right: { style: undefined },
      };
    });

    //  Time slots
    TIME_SLOTS.forEach((slot, i) => {
      const row = ROW_OFFSET + i;
      const wsRow = ws.getRow(row);
      wsRow.height = SLOT_HEIGHT;
      wsRow.hidden = false;

      const cell = ws.getCell(row, TIME_COL);
      const isFullHour = slot.endsWith('h');
      const isHalfHour = slot.endsWith('h30');
      cell.value = isFullHour || isHalfHour ? slot : '';
      cell.alignment = { horizontal: 'left', vertical: 'top' };
      cell.font = { bold: false, size: 9 };
    });

    //  STEP 1: collect all block data and mark occupied
    type BlockData = {
      col: number;
      startRow: number;
      endRow: number;
      richText: ExcelJS.CellRichTextValue;
      color: string;
    };

    const blocks: BlockData[] = [];

    sortedRooms.forEach((room, i) => {
      const col = TIME_COL + 1 + i * 2;
      const dayScheds = room.schedules.filter((s: any) => s.date.slice(0, 10) === date);

      for (const sched of dayScheds) {
        const startSlot = this.slotIndex(sched.startTime, minMinutes);
        const span = this.slotSpan(sched.startTime, sched.endTime);

        const startRow = ROW_OFFSET + startSlot;
        const endRow = Math.min(startRow + span - 1, ROW_OFFSET + TIME_SLOTS.length - 1);

        if (!this.isRangeFree(occupied, col, startRow, endRow)) {
          continue;
        }

        const { richText, color } = this.buildScheduleContent(sched, span);

        blocks.push({ col, startRow, endRow, richText, color });
        this.markRange(occupied, col, startRow, endRow);
      }
    });

    //  STEP 2: draw empty cell borders
    const noBorder = {
      top: { style: undefined },
      bottom: { style: undefined },
      left: { style: undefined },
      right: { style: undefined },
    };

    for (let r = ROW_OFFSET; r < ROW_OFFSET + TIME_SLOTS.length; r++) {
      for (let c = TIME_COL + 1; c <= lastCol; c++) {
        const isSpacerCol = (c - TIME_COL) % 2 === 0;
        const colOccupied = occupied.get(c);

        if (!colOccupied?.has(r)) {
          ws.getCell(r, c).border = isSpacerCol
            ? noBorder
            : {
                top: { style: undefined },
                bottom: { style: undefined },
                left: { style: 'medium' },
                right: { style: 'medium' },
              };
        }
      }
    }

    //  STEP 3: clear spacer cols in row 2
    sortedRooms.forEach((_, i) => {
      const spacerCol = TIME_COL + 2 + i * 2;
      ws.getCell(2, spacerCol).border = {
        top: { style: undefined },
        bottom: { style: undefined },
        left: { style: undefined },
        right: { style: undefined },
      };
    });

    //  STEP 4: draw block content
    for (const block of blocks) {
      const { col, startRow, endRow, richText, color } = block;

      if (endRow > startRow) {
        ws.mergeCells(startRow, col, endRow, col);
      }

      const cell = ws.getCell(startRow, col);
      cell.value = richText;
      cell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
      cell.font = { size: 8 };
      cell.fill = this.fill(color);
    }

    for (const block of blocks) {
      const { col, startRow, endRow } = block;

      for (let r = startRow; r <= endRow; r++) {
        ws.getCell(r, col).border = {
          top: r === startRow ? { style: 'medium' } : { style: undefined },
          bottom: r === endRow ? { style: 'medium' } : { style: undefined },
          left: { style: 'medium' },
          right: { style: 'medium' },
        };
      }
    }

    //  STEP 6:  re-apply top border on every block start row
    for (const block of blocks) {
      const cell = ws.getCell(block.startRow, block.col);
      cell.border = {
        ...cell.border,
        top: { style: 'medium' },
      };
    }
  }

  private buildScheduleContent(s: any, span: number): { richText: ExcelJS.CellRichTextValue; color: string } {
    const da = s.doctoragenda;
    const parts: ExcelJS.RichText[] = [];

    if (span <= 1) {
      if (da?.surgeon) {
        parts.push({
          text: `${da.surgeon.lastName} ${da.surgeon.firstName[0]}. \n`,
          font: { size: 7 },
        });
      }
      parts.push({
        text: `${s.type === 'monitoring' ? 'Surv.' : 'Prép.'}\n`,
        font: { size: 7, bold: true },
      });
      if (da?.admissionType) {
        parts.push({
          text: `${da.admissionType}\n`,
          font: { size: 7, bold: true },
        });
      }
      if (da?.specialty?.name) {
        parts.push({
          text: `${da.specialty.name}\n`,
          font: { size: 7 },
        });
      }
      if (da?.room?.roomNumber) {
        parts.push({
          text: `S${da?.room?.roomNumber}\n`,
          font: { size: 8 },
        });
      }
      if (da?.patientAge && da?.patientAge !== '') {
        parts.push({
          text: `${da.patientAge} ans`,
          font: { size: 8 },
        });
      }
    } else if (span === 2) {
      if (da?.surgeon) {
        parts.push({
          text: `${da.surgeon.lastName} ${da.surgeon.firstName[0]}.\n`,
          font: { size: 7 },
        });
      }
      if (da?.specialty?.name) {
        parts.push({
          text: `${da.specialty.name}\n`,
          font: { size: 7 },
        });
      }
      parts.push({
        text: `${s.type === 'monitoring' ? 'Surveillance' : 'Préparation'}\n`,
        font: { size: 7, bold: true },
      });
      if (da?.admissionType) {
        parts.push({
          text: `${da.admissionType}\n`,
          font: { size: 7, bold: true },
        });
      }
      if (da?.room?.roomNumber) {
        parts.push({
          text: `S${da?.room?.roomNumber}\n`,
          font: { size: 8 },
        });
      }
      if (da?.patientAge && da?.patientAge !== '') {
        parts.push({
          text: `${da.patientAge} ans`,
          font: { size: 8 },
        });
      }
    } else {
      if (da?.surgeon) {
        parts.push({
          text: `${da.surgeon.lastName} ${da.surgeon.firstName[0]}.\n`,
          font: { size: 8 },
        });
      }
      if (da?.specialty?.name) {
        parts.push({
          text: `${da.specialty.name}\n`,
          font: { size: 8 },
        });
      }
      if (da?.room?.roomNumber) {
        parts.push({
          text: `S${da?.room?.roomNumber}\n`,
          font: { size: 8 },
        });
      }
      if (da?.patientAge && da?.patientAge !== '') {
        parts.push({
          text: `${da.patientAge} ans\n`,
          font: { size: 8 },
        });
      }
      if (da?.interventionType) {
        parts.push({
          text: `${da.interventionType}\n`,
          font: { size: 8 },
        });
      }
      parts.push({
        text: `${s.type === 'monitoring' ? 'Surveillance' : 'Préparation'}\n`,
        font: { size: 8, bold: true },
      });
      if (da?.admissionType) {
        parts.push({
          text: `${da.admissionType}`,
          font: { size: 8, bold: true },
        });
      }
    }

    let color = 'FFFF0000';
    if (s.type === 'preparation') {
      color = 'FFFFC000';
    }
    if (da?.specialty?.color) {
      color = this.hexToARGB(da.specialty.color);
    }

    return { richText: { richText: parts }, color };
  }

  private buildTimeSlots(rooms: any[], date: string): { slots: string[]; minMinutes: number } {
    let minMinutes = Infinity;
    let maxMinutes = -Infinity;

    for (const room of rooms) {
      if (room.recoveryRoom?.openingTime) {
        const d = new Date(room.recoveryRoom.openingTime);
        const openMin = d.getUTCHours() * 60 + d.getUTCMinutes();
        minMinutes = Math.min(minMinutes, openMin);
      } else {
        minMinutes = Math.min(minMinutes, 7 * 60);
      }

      if (room.recoveryRoom?.closingTime) {
        const d = new Date(room.recoveryRoom.closingTime);
        const closeMin = d.getUTCHours() * 60 + d.getUTCMinutes();
        maxMinutes = Math.max(maxMinutes, closeMin);
      }

      for (const s of room.schedules) {
        if (s.date.slice(0, 10) !== date) {
          continue;
        }

        const start = new Date(s.startTime);
        const end = new Date(s.endTime);

        const startMin = start.getUTCHours() * 60 + start.getUTCMinutes();
        const endMin = end.getUTCHours() * 60 + end.getUTCMinutes();

        minMinutes = Math.min(minMinutes, startMin);
        maxMinutes = Math.max(maxMinutes, endMin);
      }
    }

    if (!isFinite(minMinutes)) {
      minMinutes = 7 * 60;
    }
    if (!isFinite(maxMinutes)) {
      maxMinutes = 17 * 60;
    }

    minMinutes = Math.floor(minMinutes / 15) * 15;
    maxMinutes = Math.ceil(maxMinutes / 15) * 15;

    const slots: string[] = [];

    for (let t = minMinutes; t <= maxMinutes; t += 15) {
      const h = Math.floor(t / 60);
      const m = t % 60;

      const isLabeled = m === 0 || m === 30;
      slots.push(isLabeled ? `${h}h${m === 0 ? '' : '30'}` : '');
    }

    return { slots, minMinutes };
  }

  private slotIndex(iso: string, baseMinutes: number): number {
    const d = new Date(iso);
    const minutes = d.getUTCHours() * 60 + d.getUTCMinutes();
    return Math.round((minutes - baseMinutes) / 15);
  }

  private slotSpan(start: string, end: string): number {
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    return Math.max(1, Math.round((e - s) / (1000 * 60 * 15)));
  }

  private fill(argb: string): ExcelJS.Fill {
    return {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb },
    };
  }

  private hexToARGB(hex: string): string {
    return 'FF' + hex.replace('#', '').toUpperCase().padStart(6, '0');
  }

  private formatDateShort(date: string): string {
    const d = new Date(date);
    return d.toLocaleDateString('fr-FR');
  }

  private sanitizeSheetName(name: string): string {
    return name.replace(/[*?:\\/[\]]/g, '-').slice(0, 31);
  }

  private isRangeFree(occupied: Map<number, Set<number>>, col: number, startRow: number, endRow: number): boolean {
    const set = occupied.get(col);
    if (!set) {
      return true;
    }

    for (let r = startRow; r <= endRow; r++) {
      if (set.has(r)) {
        return false;
      }
    }
    return true;
  }

  private markRange(occupied: Map<number, Set<number>>, col: number, startRow: number, endRow: number): void {
    if (!occupied.has(col)) {
      occupied.set(col, new Set());
    }

    const set = occupied.get(col)!;
    for (let r = startRow; r <= endRow; r++) {
      set.add(r);
    }
  }
}

type TextStyle = {
  bold?: boolean;
  underline?: boolean;
  textColor?: [number, number, number];
  bgColor?: [number, number, number];
};

type HtmlToken = { type: 'text'; text: string; style: TextStyle } | { type: 'newline' };
